<template>
  <v-dialog fullscreen :value="true">
    <v-card dark>
      <v-container style="max-width: 1700px" fluid class="pa-4 pt-10">
        <v-row class="mb-4">
          <v-col class="d-flex">
            <v-btn @click="$router.push('/dashboard')" large class="white mr-5" color="white" icon
              ><v-icon color="black">mdi-chevron-left</v-icon></v-btn
            >
            <span class="text-h4 font-weight-bold white--text">PLANNING & CONTROLLING</span>
          </v-col>
        </v-row>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-6" dark>
          <v-tab>Standort-Planung</v-tab>
          <v-tab>Controlling (Plan vs. Ist)</v-tab>
          <v-tab><v-icon left>mdi-view-dashboard</v-icon>Cockpit</v-tab>
          <v-tab>
            <v-icon left>mdi-file-import-outline</v-icon>
            CSV-Import
            <v-chip v-if="pendingImportsCount > 0" small color="warning" class="ml-2">
              {{ pendingImportsCount }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab" class="transparent">
          <v-tab-item>
            <v-card outlined class="mb-4" dark>
              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="planData.studioId"
                      :items="mappedStudios"
                      item-text="name"
                      item-value="_id"
                      label="Studio auswählen"
                      outlined
                      dense
                      dark></v-select>
                  </v-col>
                  <v-col cols="12" sm="4" md="2">
                    <v-select v-model="planData.year" :items="availableYears" label="Planungsjahr" outlined dense dark></v-select>
                  </v-col>
                  <v-col cols="12" sm="4" md="2">
                    <v-select
                      v-model="planData.startMonth"
                      :items="months"
                      item-text="label"
                      item-value="key"
                      label="Planungs-Start (Eröffnung/Übernahme)"
                      outlined
                      dense
                      dark></v-select>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col cols="12" md="3" class="d-flex justify-end align-center">
                    <v-btn color="primary" depressed @click="loadLocationPlan" :disabled="!planData.studioId || !planData.year">
                      <v-icon left>mdi-cloud-download</v-icon> Plan abrufen
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center justify-end" v-if="isEditing">
                    <v-switch
                      v-model="viewMode"
                      true-value="calculated"
                      false-value="input"
                      label="Berechnete Ansicht"
                      color="success"
                      dark
                      hide-details></v-switch>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <div v-if="isEditing && viewMode === 'input'">
              <v-card outlined class="mb-6" dark>
                <v-card-title>Mitglieder-Planung</v-card-title>
                <v-card-text>
                  <v-row class="mb-4">
                    <v-col cols="12" sm="4">
                      <v-text-field
                        v-model.number="planData.members.baseJan"
                        :label="'Stand Anfang ' + (months.find((m) => m.key === planData.startMonth)?.label || 'Januar')"
                        type="number"
                        outlined
                        dense
                        dark></v-text-field>
                    </v-col>
                  </v-row>

                  <v-simple-table dark class="transparent-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Kategorie</th>
                          <th v-for="month in months" :key="month.key">{{ month.label }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style="min-width: 100px" class="font-weight-bold">Zugänge</td>
                          <td v-for="month in months" :key="'join-' + month.key">
                            <v-text-field
                              v-model.number="planData.members.joined[month.key]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input"></v-text-field>
                          </td>
                        </tr>
                        <tr>
                          <td style="min-width: 100px" class="font-weight-bold">Abgänge</td>
                          <td v-for="month in months" :key="'left-' + month.key">
                            <v-text-field
                              v-model.number="planData.members.left[month.key]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input"></v-text-field>
                          </td>
                        </tr>
                        <tr>
                          <td class="font-weight-bold" style="min-width: 100px">Ruhend (%)</td>
                          <td v-for="month in months" :key="'pause-' + month.key">
                            <v-text-field
                              v-model.number="planData.members.pausedRate[month.key]"
                              type="number"
                              step="0.1"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input"></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>

              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined class="fill-height" dark>
                    <v-card-title>Umsatz-Planung (Werte pro Mitglied / pro Monat)</v-card-title>
                    <v-card-text>
                      <v-simple-table dense dark class="transparent-table">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Position</th>
                              <th class="text-right" style="width: 120px">Betrag</th>
                              <th class="text-right" style="width: 120px">Steig. p.a. (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="item in revenueItems" :key="item.key">
                              <td class="text-left" style="cursor: pointer" @click="openMonthlyDialog('revenue', item)">
                                {{ item.label }}
                                <v-icon
                                  v-if="hasExtraMonthly('revenue', item)"
                                  small
                                  color="info"
                                  class="ml-1"
                                  title="Zusätzliche Monatsbeträge hinterlegt">
                                  mdi-calendar-plus
                                </v-icon>
                              </td>
                              <td>
                                <v-text-field
                                  v-model.number="planData.revenue[item.key]"
                                  type="number"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                              </td>
                              <td>
                                <v-text-field
                                  v-model.number="planData.revenue.growthRates[item.key]"
                                  type="number"
                                  step="0.1"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined class="fill-height" dark>
                    <v-card-title>Kosten-Planung (Werte pro Monat)</v-card-title>
                    <v-card-text>
                      <v-simple-table dense dark class="transparent-table">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left">Position</th>
                              <th class="text-right" style="width: 120px">Betrag</th>
                              <th class="text-right" style="width: 120px">Steig. p.a. (%)</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="item in costItems" :key="item.key">
                              <td class="text-left" style="cursor: pointer" @click="openMonthlyDialog('costs', item)">
                                {{ item.label }}
                                <v-icon
                                  v-if="hasExtraMonthly('costs', item)"
                                  small
                                  color="info"
                                  class="ml-1"
                                  title="Zusätzliche Monatsbeträge hinterlegt">
                                  mdi-calendar-plus
                                </v-icon>
                              </td>
                              <td>
                                <v-text-field
                                  v-if="item.isNested"
                                  v-model.number="planData.costs[item.isNested][item.nestedKey]"
                                  type="number"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                                <v-text-field
                                  v-else
                                  v-model.number="planData.costs[item.key]"
                                  type="number"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                              </td>
                              <td>
                                <v-text-field
                                  v-if="item.isNested"
                                  v-model.number="planData.costs.growthRates[item.isNested][item.nestedKey]"
                                  type="number"
                                  step="0.1"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                                <v-text-field
                                  v-else
                                  v-model.number="planData.costs.growthRates[item.key]"
                                  type="number"
                                  step="0.1"
                                  dense
                                  hide-details
                                  outlined
                                  dark
                                  class="compact-input my-1"></v-text-field>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col class="text-right">
                  <v-btn color="success" large depressed @click="savePlan" :loading="isSaving">
                    <v-icon left>mdi-content-save</v-icon> Plan speichern
                  </v-btn>
                </v-col>
              </v-row>

              <v-dialog v-model="editMonthlyDialog.isOpen" max-width="600px">
                <v-card dark>
                  <v-card-title>
                    Zusätzliche Monatsbeträge erfassen
                    <v-spacer></v-spacer>
                    <v-btn icon @click="editMonthlyDialog.isOpen = false">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-subtitle>Position: {{ editMonthlyDialog.label }}</v-card-subtitle>
                  <v-card-text>
                    <p class="text-body-2 mb-4">
                      Hier kannst du für jeden Monat spezifische Zusatzbeträge erfassen, die auf den Basis-Jahreswert / Monatsdurchschnitt aufaddiert
                      werden. Dies ist optional.
                    </p>
                    <v-row>
                      <v-col cols="4" sm="3" v-for="month in months" :key="month.key">
                        <v-text-field
                          v-model.number="getEditTarget()[month.key]"
                          :label="month.label"
                          type="number"
                          dense
                          outlined
                          dark></v-text-field>
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="editMonthlyDialog.isOpen = false">Fertig</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>

            <!-- BERECHNETE ANSICHT -->
            <div v-else-if="isEditing && viewMode === 'calculated'" class="calculated-view">
              <!-- Mitglieder-Planung -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-card-title class="primary white--text font-weight-bold">Mitglieder-Planung</v-card-title>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Bereich
                        </th>
                        <th class="text-right info-col annual-col">Jahreswert / Ø</th>
                        <th v-for="m in months" :key="m.key" class="text-right">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="header-col pl-4">Stand Anfang des Monats</td>
                        <td class="text-right info-col annual-col"></td>
                        <td v-for="(m, i) in calculatedData.members" :key="'start-' + i" class="text-right">{{ formatMoney(m.start) }}</td>
                      </tr>
                      <tr>
                        <td class="header-col pl-4 text-success">Zugänge (vertragswirksam)</td>
                        <td class="text-right info-col text-success annual-col">{{ formatMoney(calculatedData.kpi.yearlyJoined) }}</td>
                        <td v-for="(m, i) in calculatedData.members" :key="'join-' + i" class="text-right text-success">
                          {{ formatMoney(m.joined) }}
                        </td>
                      </tr>
                      <tr>
                        <td class="header-col pl-4 text-error">Abgänge (vertragswirksam)</td>
                        <td class="text-right info-col text-error annual-col">{{ formatMoney(calculatedData.kpi.yearlyLeft) }}</td>
                        <td v-for="(m, i) in calculatedData.members" :key="'left-' + i" class="text-right text-error">{{ formatMoney(m.left) }}</td>
                      </tr>
                      <tr class="font-weight-bold highlight-row">
                        <td class="header-col pl-4">Stand Ende des Monats</td>
                        <td class="text-right info-col annual-col"></td>
                        <td v-for="(m, i) in calculatedData.members" :key="'end-' + i" class="text-right">{{ formatMoney(m.end) }}</td>
                      </tr>

                      <tr>
                        <td class="header-col pl-4">Ruhend</td>
                        <td class="text-right info-col annual-col"></td>
                        <td v-for="(m, i) in calculatedData.members" :key="'pause-' + i" class="text-right">{{ formatMoney(m.paused) }}</td>
                      </tr>
                      <tr>
                        <td class="header-col pl-4">Ruhenden Quote</td>
                        <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyPausedRate) }}</td>
                        <td v-for="(m, i) in calculatedData.members" :key="'pauseRate-' + i" class="text-right">{{ formatPercent(m.pausedRate) }}</td>
                      </tr>
                      <tr>
                        <td class="header-col pl-4">Fluktuationsquote</td>
                        <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyFluctuation) }}</td>
                        <td v-for="(m, i) in calculatedData.members" :key="'fluc-' + i" class="text-right">{{ formatPercent(m.fluctuation) }}</td>
                      </tr>
                      <tr>
                        <td class="header-col pl-4">Wachstum</td>
                        <td class="text-right info-col annual-col">{{ formatPercent(calculatedData.kpi.yearlyGrowthRate) }}</td>
                        <td v-for="(m, i) in calculatedData.members" :key="'growth-' + i" class="text-right">{{ formatPercent(m.growth) }}</td>
                      </tr>
                      <tr class="highlight-row">
                        <td class="header-col pl-4">Wachstum absolut</td>
                        <td
                          class="text-right info-col font-weight-bold annual-col"
                          :class="calculatedData.kpi.yearlyGrowth < 0 ? 'text-error' : 'text-success'">
                          {{ calculatedData.kpi.yearlyGrowth > 0 ? "+" : "" }}{{ formatMoney(calculatedData.kpi.yearlyGrowth) }}
                        </td>
                        <td
                          v-for="(m, i) in calculatedData.members"
                          :key="'abs-' + i"
                          class="text-right"
                          :class="m.absGrowth < 0 ? 'text-error' : 'text-success'">
                          {{ m.absGrowth > 0 ? "+" : "" }}{{ formatMoney(m.absGrowth) }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Umsatz-Planung -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Umsatz-Planung
                        </th>
                        <th class="text-right info-col" width="100">Pro Mitglied</th>
                        <th class="text-right info-col" width="100">Steigerung p.a. in %</th>
                        <th class="text-right info-col" width="100">% vom Umsatz</th>
                        <th class="text-right info-col annual-col" width="100">Jahreswert</th>
                        <th v-for="m in months" :key="m.key" class="text-right" width="80">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in revenueItems" :key="'rev-' + item.key">
                        <td class="header-col pl-4">{{ item.label }}</td>
                        <td class="text-right info-col">
                          {{ calculatedData.revenueAvgPerMember[item.key] ? calculatedData.revenueAvgPerMember[item.key].toFixed(1) : "" }}
                        </td>
                        <td class="text-right info-col">
                          {{
                            formatPercent(
                              (planData.revenue.growthRates && planData.revenue.growthRates[item.key] ? planData.revenue.growthRates[item.key] : 0) /
                                100,
                            )
                          }}
                        </td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.revenueYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.revenueYearly[item.key]) }}</td>
                        <td v-for="(val, i) in calculatedData.revenue[item.key]" :key="'rev-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>
                      <tr class="font-weight-black highlight-row total-row">
                        <td class="header-col pl-4">GESAMT ERLÖSE</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.kpi.yearlyTotalRevenue) }}</td>
                        <td v-for="(val, i) in calculatedData.kpi.gesamtErloese" :key="'tot-rev-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Kosten-Planung -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Kosten-Planung
                        </th>
                        <th class="text-right info-col" width="100">pro Monat</th>
                        <th class="text-right info-col" width="100">Steigerung p.a. in %</th>
                        <th class="text-right info-col" width="100">% vom Umsatz</th>
                        <th class="text-right info-col annual-col" width="100">Jahreswert</th>
                        <th v-for="m in months" :key="m.key" class="text-right" width="80">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="header-col pl-4">{{ costItems.find((c) => c.key === "cogs").label }}</td>
                        <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly["cogs"]) }}</td>
                        <td class="text-right info-col">
                          {{
                            formatPercent(
                              (planData.costs.growthRates && planData.costs.growthRates["cogs"] ? planData.costs.growthRates["cogs"] : 0) / 100,
                            )
                          }}
                        </td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.costsYearly["cogs"] / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly["cogs"]) }}</td>
                        <td v-for="(val, i) in calculatedData.costs['cogs']" :key="'cogs-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>
                      <tr class="font-weight-bold highlight-row">
                        <td class="header-col pl-4">Rohertrag</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col annual-col">
                          {{ formatMoney(calculatedData.kpi.yearlyTotalRevenue - calculatedData.costsYearly["cogs"]) }}
                        </td>
                        <td v-for="(val, i) in calculatedData.kpi.rohertrag" :key="'rohe-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>

                      <!-- OPEX Costs -->
                      <tr v-for="item in opexItems" :key="'opex-' + item.key">
                        <td class="header-col pl-4">{{ item.label }}</td>
                        <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly[item.key]) }}</td>
                        <td class="text-right info-col">
                          {{
                            formatPercent(
                              ((planData.costs.growthRates &&
                                (item.isNested ? planData.costs.growthRates[item.isNested][item.nestedKey] : planData.costs.growthRates[item.key])) ||
                                0) / 100,
                            )
                          }}
                        </td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.costsYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly[item.key]) }}</td>
                        <td v-for="(val, i) in calculatedData.costs[item.key]" :key="'opex-val-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>

                      <tr class="font-weight-bold highlight-row text-error">
                        <td class="header-col pl-4">Gesamtkosten</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.kpi.yearlyOpex / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.kpi.yearlyOpex) }}</td>
                        <td v-for="(val, i) in calculatedData.kpi.gesamtkosten" :key="'tot-cost-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>

                      <tr class="font-weight-black highlight-row total-row">
                        <td class="header-col pl-4">EBIT DA</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyEbitda < 0 ? 'text-error' : ''">
                          {{ formatMoney(calculatedData.kpi.yearlyEbitda) }}
                        </td>
                        <td
                          v-for="(val, i) in calculatedData.kpi.ebitda"
                          :key="'ebitda-' + i"
                          class="text-right"
                          :class="val < 0 ? 'text-error' : ''">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>

                      <tr>
                        <td class="header-col pl-4">{{ costItems.find((c) => c.key === "depreciation").label }}</td>
                        <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly["depreciation"]) }}</td>
                        <td class="text-right info-col">
                          {{
                            formatPercent(
                              (planData.costs.growthRates && planData.costs.growthRates["depreciation"]
                                ? planData.costs.growthRates["depreciation"]
                                : 0) / 100,
                            )
                          }}
                        </td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.costsYearly["depreciation"] / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly["depreciation"]) }}</td>
                        <td v-for="(val, i) in calculatedData.costs['depreciation']" :key="'depr-' + i" class="text-right">{{ formatMoney(val) }}</td>
                      </tr>

                      <tr class="font-weight-black highlight-row total-row">
                        <td class="header-col pl-4">EBIT</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyEbit < 0 ? 'text-error' : ''">
                          {{ formatMoney(calculatedData.kpi.yearlyEbit) }}
                        </td>
                        <td v-for="(val, i) in calculatedData.kpi.ebit" :key="'ebit-' + i" class="text-right" :class="val < 0 ? 'text-error' : ''">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>

                      <!-- Non OPEX Items -->
                      <tr v-for="item in nonOpexItems" :key="'nonopex-' + item.key">
                        <td class="header-col pl-4">{{ item.label }}</td>
                        <td class="text-right info-col">{{ formatMoney(calculatedData.costsMonthly[item.key]) }}</td>
                        <td class="text-right info-col">
                          {{
                            formatPercent(
                              (planData.costs.growthRates && planData.costs.growthRates[item.key] ? planData.costs.growthRates[item.key] : 0) / 100,
                            )
                          }}
                        </td>
                        <td class="text-right info-col">
                          {{ formatPercent(calculatedData.costsYearly[item.key] / calculatedData.kpi.yearlyTotalRevenue) }}
                        </td>
                        <td class="text-right info-col annual-col">{{ formatMoney(calculatedData.costsYearly[item.key]) }}</td>
                        <td v-for="(val, i) in calculatedData.costs[item.key]" :key="'nonopex-val-' + i" class="text-right">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>

                      <tr class="font-weight-black highlight-row result-row">
                        <td class="header-col pl-4">Ergebnis nach Steuern</td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col"></td>
                        <td class="text-right info-col annual-col" :class="calculatedData.kpi.yearlyErgebnis < 0 ? 'text-error' : 'text-success'">
                          {{ formatMoney(calculatedData.kpi.yearlyErgebnis) }}
                        </td>
                        <td
                          v-for="(val, i) in calculatedData.kpi.ergebnisNachSteuern"
                          :key="'ergebnis-' + i"
                          class="text-right"
                          :class="val < 0 ? 'text-error' : 'text-success'">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </div>
          </v-tab-item>

          <v-tab-item>
            <v-card outlined class="mb-4" dark>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" sm="6" md="4">
                    <v-select
                      v-model="controllingStudioIds"
                      :items="mappedStudios"
                      item-text="name"
                      item-value="_id"
                      label="Studios auswählen (Mehrfach möglich)"
                      outlined
                      dense
                      dark
                      multiple
                      chips
                      small-chips
                      clearable>
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleAllControllingStudios">
                          <v-list-item-action>
                            <v-icon :color="controllingStudioIds.length === mappedStudios.length ? 'primary' : ''">
                              {{ controllingStudioIds.length === mappedStudios.length ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content><v-list-item-title>Alle Studios</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="2">
                    <v-select v-model="controllingYear" :items="availableYears" label="Planungsjahr" outlined dense dark></v-select>
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn
                      color="primary"
                      depressed
                      @click="loadControllingData"
                      :loading="isControllingLoading"
                      :disabled="!controllingStudioIds.length || !controllingYear">
                      <v-icon left>mdi-chart-line</v-icon> Controlling laden
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <div v-if="controllingCalculatedPlanObj && controllingCalculatedActualsObj" class="calculated-view">
              <!-- Mitglieder-Controlling -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-card-title class="primary white--text font-weight-bold">Mitglieder-Controlling</v-card-title>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Bereich
                        </th>
                        <th class="text-right info-col annual-col">Jahreswert / Ø</th>
                        <th v-for="m in months" :key="m.key" class="text-right">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="header-col pl-4 text-success">Zugänge (PLAN)</td>
                        <td class="text-right info-col text-success annual-col">{{ formatMoney(controllingCalculatedPlanObj.kpi.yearlyJoined) }}</td>
                        <td v-for="(m, i) in controllingCalculatedPlanObj.members" :key="'join-plan-' + i" class="text-right text-success">
                          {{ formatMoney(m.joined) }}
                        </td>
                      </tr>
                      <tr class="ist-row">
                        <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST Zugänge</td>
                        <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                          {{ formatMoney(controllingCalculatedActualsObj.kpi?.yearlyJoined || 0) }}
                          <span
                            :class="
                              getDeltaClass(
                                controllingCalculatedActualsObj.kpi?.yearlyJoined || 0,
                                controllingCalculatedPlanObj.kpi.yearlyJoined,
                                false,
                              )
                            "
                            class="ml-1 text-caption">
                            {{ formatDelta(controllingCalculatedActualsObj.kpi?.yearlyJoined || 0, controllingCalculatedPlanObj.kpi.yearlyJoined) }}
                          </span>
                        </td>
                        <td
                          v-for="(val, i) in controllingCalculatedActualsObj.members?.joined || new Array(12).fill(0)"
                          :key="'join-ist-' + i"
                          class="text-right grey--text text--lighten-1">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.members[i].joined, false)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.members[i].joined) }}
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td class="header-col pl-4 text-error">Abgänge (PLAN)</td>
                        <td class="text-right info-col text-error annual-col">{{ formatMoney(controllingCalculatedPlanObj.kpi.yearlyLeft) }}</td>
                        <td v-for="(m, i) in controllingCalculatedPlanObj.members" :key="'left-plan-' + i" class="text-right text-error">
                          {{ formatMoney(m.left) }}
                        </td>
                      </tr>
                      <tr class="ist-row">
                        <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST Abgänge</td>
                        <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                          {{ formatMoney(controllingCalculatedActualsObj.kpi?.yearlyLeft || 0) }}
                          <span
                            :class="
                              getDeltaClass(controllingCalculatedActualsObj.kpi?.yearlyLeft || 0, controllingCalculatedPlanObj.kpi.yearlyLeft, true)
                            "
                            class="ml-1 text-caption">
                            {{ formatDelta(controllingCalculatedActualsObj.kpi?.yearlyLeft || 0, controllingCalculatedPlanObj.kpi.yearlyLeft) }}
                          </span>
                        </td>
                        <td
                          v-for="(val, i) in controllingCalculatedActualsObj.members?.left || new Array(12).fill(0)"
                          :key="'left-ist-' + i"
                          class="text-right grey--text text--lighten-1">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.members[i].left, true)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.members[i].left) }}
                          </div>
                        </td>
                      </tr>

                      <tr class="font-weight-bold highlight-row">
                        <td class="header-col pl-4">Stand Ende des Monats (PLAN)</td>
                        <td class="text-right info-col annual-col"></td>
                        <td v-for="(m, i) in controllingCalculatedPlanObj.members" :key="'end-plan-' + i" class="text-right">
                          {{ formatMoney(m.end) }}
                        </td>
                      </tr>
                      <tr class="font-weight-bold highlight-row ist-row">
                        <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST Stand Ende des Monats</td>
                        <td class="text-right info-col annual-col"></td>
                        <td
                          v-for="(val, i) in controllingCalculatedActualsObj.members?.end || new Array(12).fill(0)"
                          :key="'end-ist-' + i"
                          class="text-right grey--text text--lighten-1">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.members[i].end, false)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.members[i].end) }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Umsatz-Controlling -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-card-title class="primary white--text font-weight-bold">Umsatz-Controlling</v-card-title>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Bereich
                        </th>
                        <th class="text-right info-col annual-col">Jahreswert</th>
                        <th v-for="m in months" :key="m.key" class="text-right">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="item in revenueItems">
                        <!-- Umsatz PLAN -->
                        <tr :key="'rev-plan-' + item.key">
                          <td class="header-col pl-4">{{ item.label }} (PLAN)</td>
                          <td class="text-right info-col annual-col">{{ formatMoney(controllingCalculatedPlanObj.revenueYearly[item.key]) }}</td>
                          <td v-for="(val, i) in controllingCalculatedPlanObj.revenue[item.key]" :key="'rev-val-' + i" class="text-right">
                            {{ formatMoney(val) }}
                          </td>
                        </tr>
                        <!-- Umsatz IST -->
                        <tr :key="'rev-ist-' + item.key" class="ist-row">
                          <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST</td>
                          <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                            {{ formatMoney(controllingCalculatedActualsObj.revenueYearly[item.key] || 0) }}
                            <span
                              :class="
                                getDeltaClass(
                                  controllingCalculatedActualsObj.revenueYearly[item.key] || 0,
                                  controllingCalculatedPlanObj.revenueYearly[item.key],
                                  false,
                                )
                              "
                              class="ml-1 text-caption">
                              {{
                                formatDelta(
                                  controllingCalculatedActualsObj.revenueYearly[item.key] || 0,
                                  controllingCalculatedPlanObj.revenueYearly[item.key],
                                )
                              }}
                            </span>
                          </td>
                          <td
                            v-for="(val, i) in controllingCalculatedActualsObj.revenue[item.key] || new Array(12).fill(0)"
                            :key="'rev-val-ist-' + i"
                            class="text-right grey--text text--lighten-1 clickable-cell"
                            style="cursor: pointer"
                            @click="openManualRevenueModal(item, i)">
                            {{ formatMoney(val) }}
                            <div
                              :class="getDeltaClass(val, controllingCalculatedPlanObj.revenue[item.key][i], false)"
                              class="text-caption"
                              style="line-height: 1">
                              {{ formatDelta(val, controllingCalculatedPlanObj.revenue[item.key][i]) }}
                            </div>
                          </td>
                        </tr>
                      </template>

                      <tr class="font-weight-bold highlight-row total-row">
                        <td class="header-col pl-4">Gesamterlöse (PLAN)</td>
                        <td class="text-right info-col annual-col">{{ formatMoney(controllingCalculatedPlanObj.kpi.yearlyTotalRevenue) }}</td>
                        <td v-for="(val, i) in controllingCalculatedPlanObj.kpi.gesamtErloese" :key="'tot-rev-plan-' + i" class="text-right">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>
                      <!-- NEU: IST Gesamterlöse -->
                      <tr class="font-weight-bold highlight-row grey--text text--lighten-1">
                        <td class="header-col pl-6" style="font-size: 12px !important">↳ IST Gesamterlöse</td>
                        <td class="text-right info-col annual-col">
                          {{ formatMoney(controllingCalculatedActualsObj.kpi.yearlyTotalRevenue) }}
                          <span
                            :class="
                              getDeltaClass(
                                controllingCalculatedActualsObj.kpi.yearlyTotalRevenue,
                                controllingCalculatedPlanObj.kpi.yearlyTotalRevenue,
                                false,
                              )
                            "
                            class="ml-1 text-caption">
                            {{
                              formatDelta(controllingCalculatedActualsObj.kpi.yearlyTotalRevenue, controllingCalculatedPlanObj.kpi.yearlyTotalRevenue)
                            }}
                          </span>
                        </td>
                        <td v-for="(val, i) in controllingCalculatedActualsObj.kpi.gesamtErloese" :key="'tot-rev-ist-' + i" class="text-right">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.kpi.gesamtErloese[i], false)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.kpi.gesamtErloese[i]) }}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <!-- Kosten-Controlling -->
              <v-card outlined class="mb-6 calculated-card" dark>
                <v-card-title class="primary white--text font-weight-bold">Kosten-Controlling</v-card-title>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left font-weight-bold header-col primary--text text--lighten-1" style="font-size: 14px !important">
                          Bereich
                        </th>
                        <th class="text-right info-col annual-col">Jahreswert</th>
                        <th v-for="m in months" :key="m.key" class="text-right">{{ m.label }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="item in opexItems">
                        <!-- Kosten PLAN -->
                        <tr :key="'opex-plan-' + item.key">
                          <td class="header-col pl-4">{{ item.label }} (PLAN)</td>
                          <td class="text-right info-col annual-col">{{ formatMoney(controllingCalculatedPlanObj.costsYearly[item.key]) }}</td>
                          <td v-for="(val, i) in controllingCalculatedPlanObj.costs[item.key]" :key="'opex-val-' + i" class="text-right">
                            {{ formatMoney(val) }}
                          </td>
                        </tr>
                        <!-- Kosten IST -->
                        <tr :key="'opex-ist-' + item.key" class="ist-row">
                          <td class="header-col pl-6 grey--text text--lighten-1" style="font-size: 11px !important">↳ IST</td>
                          <td class="text-right info-col annual-col grey--text text--lighten-1 font-weight-bold">
                            {{ formatMoney(controllingCalculatedActualsObj.costsYearly[item.key] || 0) }}
                            <span
                              :class="
                                getDeltaClass(
                                  controllingCalculatedActualsObj.costsYearly[item.key] || 0,
                                  controllingCalculatedPlanObj.costsYearly[item.key],
                                  true,
                                )
                              "
                              class="ml-1 text-caption">
                              {{
                                formatDelta(
                                  controllingCalculatedActualsObj.costsYearly[item.key] || 0,
                                  controllingCalculatedPlanObj.costsYearly[item.key],
                                )
                              }}
                            </span>
                          </td>
                          <td
                            v-for="(val, i) in controllingCalculatedActualsObj.costs[item.key] || new Array(12).fill(0)"
                            :key="'opex-val-ist-' + i"
                            class="text-right grey--text text--lighten-1 clickable-cell"
                            style="cursor: pointer"
                            @click="openCostDetailModal(item, i)">
                            {{ formatMoney(val) }}
                            <div
                              :class="getDeltaClass(val, controllingCalculatedPlanObj.costs[item.key][i], true)"
                              class="text-caption"
                              style="line-height: 1">
                              {{ formatDelta(val, controllingCalculatedPlanObj.costs[item.key][i]) }}
                            </div>
                          </td>
                        </tr>
                      </template>

                      <tr class="font-weight-bold highlight-row text-error">
                        <td class="header-col pl-4">Gesamtkosten (PLAN)</td>
                        <td class="text-right info-col annual-col">{{ formatMoney(controllingCalculatedPlanObj.kpi.yearlyOpex) }}</td>
                        <td v-for="(val, i) in controllingCalculatedPlanObj.kpi.gesamtkosten" :key="'tot-cost-' + i" class="text-right">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>
                      <tr class="font-weight-bold highlight-row grey--text text--lighten-1">
                        <td class="header-col pl-6" style="font-size: 12px !important">↳ IST Gesamtkosten</td>
                        <td class="text-right info-col annual-col">
                          {{ formatMoney(controllingCalculatedActualsObj.kpi.yearlyOpex) }}
                          <span
                            :class="getDeltaClass(controllingCalculatedActualsObj.kpi.yearlyOpex, controllingCalculatedPlanObj.kpi.yearlyOpex, true)"
                            class="ml-1 text-caption">
                            {{ formatDelta(controllingCalculatedActualsObj.kpi.yearlyOpex, controllingCalculatedPlanObj.kpi.yearlyOpex) }}
                          </span>
                        </td>
                        <td v-for="(val, i) in controllingCalculatedActualsObj.kpi.gesamtkosten" :key="'tot-cost-ist-' + i" class="text-right">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.kpi.gesamtkosten[i], true)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.kpi.gesamtkosten[i]) }}
                          </div>
                        </td>
                      </tr>

                      <tr class="font-weight-black highlight-row total-row">
                        <td class="header-col pl-4">EBIT DA (PLAN)</td>
                        <td class="text-right info-col annual-col" :class="controllingCalculatedPlanObj.kpi.yearlyEbitda < 0 ? 'text-error' : ''">
                          {{ formatMoney(controllingCalculatedPlanObj.kpi.yearlyEbitda) }}
                        </td>
                        <td
                          v-for="(val, i) in controllingCalculatedPlanObj.kpi.ebitda"
                          :key="'ebitda-' + i"
                          class="text-right"
                          :class="val < 0 ? 'text-error' : ''">
                          {{ formatMoney(val) }}
                        </td>
                      </tr>
                      <!-- NEU: IST EBIT DA -->
                      <tr class="font-weight-bold highlight-row grey--text text--lighten-1">
                        <td class="header-col pl-6" style="font-size: 12px !important">↳ IST EBIT DA</td>
                        <td class="text-right info-col annual-col">
                          {{ formatMoney(controllingCalculatedActualsObj.kpi.yearlyEbitda) }}
                          <span
                            :class="
                              getDeltaClass(controllingCalculatedActualsObj.kpi.yearlyEbitda, controllingCalculatedPlanObj.kpi.yearlyEbitda, false)
                            "
                            class="ml-1 text-caption">
                            {{ formatDelta(controllingCalculatedActualsObj.kpi.yearlyEbitda, controllingCalculatedPlanObj.kpi.yearlyEbitda) }}
                          </span>
                        </td>
                        <td v-for="(val, i) in controllingCalculatedActualsObj.kpi.ebitda" :key="'ebitda-ist-' + i" class="text-right">
                          {{ formatMoney(val) }}
                          <div
                            :class="getDeltaClass(val, controllingCalculatedPlanObj.kpi.ebitda[i], false)"
                            class="text-caption"
                            style="line-height: 1">
                            {{ formatDelta(val, controllingCalculatedPlanObj.kpi.ebitda[i]) }}
                          </div>
                        </td>
                      </tr>
                      <!-- Optional: EBIT DA IST if total revenue IST exists... I skip for now directly, maybe user wants it later -->
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>

              <v-card dark v-if="hasUnmappedDocuments" class="mt-4" outlined>
                <v-card-title class="d-flex align-center">
                  <v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>
                  Nicht zugeordnete Belege
                  <v-chip class="ml-2" small color="warning" text-color="white">
                    {{ totalUnmappedCount }} · {{ formatMoney(totalUnmappedAmount) }}
                  </v-chip>
                  <v-btn icon small class="ml-2" @click="mappingDialog = true" title="Mapping anzeigen">
                    <v-icon small>mdi-information-outline</v-icon>
                  </v-btn>
                  <v-spacer />
                  <span class="caption grey--text">Diese Beträge fehlen aktuell im Controlling oben.</span>
                </v-card-title>

                <v-card-text>
                  <v-tabs v-model="unmappedTab" grow>
                    <v-tab>Ohne Kategorie ({{ unmappedView.noCategory.length }})</v-tab>
                    <v-tab>Unbekannte Kategorie ({{ unmappedView.unknownCategory.length }})</v-tab>
                    <v-tab>Fehlerhaft ({{ unmappedView.invalid.length }})</v-tab>
                  </v-tabs>

                  <v-data-table :headers="unmappedHeaders" :items="currentUnmappedList" dense :items-per-page="10">
                    <template v-slot:item.date="{ item }">
                      {{ item.date ? $moment(item.date).format("DD.MM.YYYY") : "—" }}
                    </template>
                    <template v-slot:item.amount="{ item }">
                      {{ formatMoney(item.amount || 0) }}
                    </template>
                    <template v-slot:item.category="{ item }">
                      <v-chip v-if="item.category" small>{{ item.category }}</v-chip>
                      <span v-else class="grey--text">—</span>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <div v-if="item.source === 'document'" class="d-flex justify-end">
                        <v-btn icon small @click="previewUnmappedDocument(item)" title="Vorschau">
                          <v-icon small>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn icon small @click="openUnmappedDocument(item)" title="Bearbeiten">
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>
                      <div v-else class="d-flex justify-end">
                        <v-btn icon small @click="$router.push('/dashboard/transaction-statistics')" title="Vorschau">
                          <v-icon small>mdi-share-outline</v-icon>
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </div>
          </v-tab-item>

          <!-- ==================== COCKPIT TAB ==================== -->
          <v-tab-item>
            <v-card outlined class="mb-4" dark>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" sm="6" md="4">
                    <v-select
                      v-model="cockpitStudioIds"
                      :items="mappedStudios"
                      item-text="name"
                      item-value="_id"
                      label="Studios auswählen"
                      outlined
                      dense
                      dark
                      multiple
                      chips
                      small-chips
                      clearable>
                      <template v-slot:prepend-item>
                        <v-list-item ripple @click="toggleAllCockpitStudios">
                          <v-list-item-action>
                            <v-icon :color="cockpitStudioIds.length === mappedStudios.length ? 'primary' : ''">
                              {{ cockpitStudioIds.length === mappedStudios.length ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
                            </v-icon>
                          </v-list-item-action>
                          <v-list-item-content><v-list-item-title>Alle Studios</v-list-item-title></v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-2"></v-divider>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="2">
                    <v-select v-model="cockpitYear" :items="availableYears" label="Planungsjahr" outlined dense dark></v-select>
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn color="primary" depressed @click="loadCockpitData" :loading="cockpitLoading" :disabled="!cockpitYear">
                      <v-icon left>mdi-refresh</v-icon> Cockpit laden
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <div v-if="cockpitPlanAgg">
              <!-- ======= BEREICH A: BIG FOUR ======= -->
              <div class="cockpit-section-title">&#9632; Die "Big Four" – Plan-Übersicht {{ cockpitYear }}</div>
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title"
                      ><v-icon left color="success">mdi-account-group</v-icon>Mitglieder zum Monatsende</v-card-title
                    >
                    <v-card-text>
                      <apexchart type="bar" height="240" :options="membersChartOptions" :series="membersChartSeries"> </apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title"><v-icon left color="primary">mdi-swap-vertical</v-icon>Zugänge / Abgänge</v-card-title>
                    <v-card-text>
                      <apexchart type="bar" height="240" :options="joinLeftChartOptions" :series="joinLeftChartSeries"> </apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title"><v-icon left color="cyan">mdi-cash-multiple</v-icon>EBITDA (CHF)</v-card-title>
                    <v-card-text>
                      <apexchart type="bar" height="240" :options="ebitdaChartOptions" :series="ebitdaChartSeries"> </apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title"><v-icon left color="teal">mdi-trending-up</v-icon>Gesamterlöse (CHF)</v-card-title>
                    <v-card-text>
                      <apexchart type="bar" height="240" :options="revenueChartOptions" :series="revenueChartSeries"> </apexchart>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- ======= BEREICH B: GAUGES ======= -->
              <div class="cockpit-section-title">&#9632; Produktivität – Jahres-KPIs</div>
              <v-row class="mb-4">
                <v-col cols="12" sm="6" md="4">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title">Abschlussquote (Leads → Verträge)</v-card-title>
                    <v-card-text class="text-center">
                      <apexchart type="radialBar" height="220" :options="closingRateGaugeOptions" :series="closingRateGaugeSeries"> </apexchart>
                      <v-alert type="info" dense text outlined class="mt-2 text-left" style="font-size: 11px">
                        <strong>Hinweis:</strong> Leads werden aktuell noch nicht in der Datenbank erfasst. Als Näherungswert wird die Anzahl der
                        geplanten Zugänge verwendet.
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title">Fluktuationsquote (p.a.)</v-card-title>
                    <v-card-text class="text-center">
                      <apexchart type="radialBar" height="220" :options="fluctuationGaugeOptions" :series="fluctuationGaugeSeries"> </apexchart>
                      <div class="grey--text text--lighten-1 text-caption mt-1">
                        Basierend auf <strong>yearlyFluctuation</strong> = Abgänge / Anfangsbestand
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-card dark outlined class="cockpit-chart-card">
                    <v-card-title class="cockpit-chart-title">Mitgliederwachstum (p.a.)</v-card-title>
                    <v-card-text class="text-center">
                      <apexchart type="radialBar" height="220" :options="growthRateGaugeOptions" :series="growthRateGaugeSeries"> </apexchart>
                      <div class="grey--text text--lighten-1 text-caption mt-1">Netto-Wachstum = (Zugänge − Abgänge) / Anfangsbestand</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- ======= BEREICH C: BENCHMARKING ======= -->
              <div class="cockpit-section-title">&#9632; Vergleich & Benchmarking</div>
              <v-card dark outlined class="mb-6 calculated-card">
                <v-card-title class="primary white--text font-weight-bold py-2">
                  KPI-Vergleich: Ausgewählt vs. Bester Wert vs. Gesamtdurchschnitt
                  <v-spacer></v-spacer>
                  <span class="text-caption font-weight-regular">{{ cockpitStudioIds.length }} Studio(s) ausgewählt</span>
                </v-card-title>
                <v-simple-table dense dark class="calc-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left header-col" style="font-size: 13px">KPI</th>
                        <th class="text-right info-col" style="color: #69f0ae">Ausgewählt</th>
                        <th class="text-right info-col" style="color: #ffd740">Bester Wert (Platz 1)</th>
                        <th class="text-right info-col" style="color: #82b1ff">Ø Alle Studios</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in benchmarkRows" :key="row.key">
                        <td class="header-col pl-4">{{ row.label }}</td>
                        <td class="text-right" :class="row.selectedClass">{{ row.selectedFmt }}</td>
                        <td class="text-right" style="color: #ffd740">{{ row.bestFmt }}</td>
                        <td class="text-right" style="color: #82b1ff">{{ row.avgFmt }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-card>
            </div>

            <div v-else-if="!cockpitLoading" class="text-center py-12 grey--text">
              <v-icon size="64" color="grey darken-1">mdi-view-dashboard-outline</v-icon>
              <div class="mt-4 text-h6">Bitte Studios und Jahr auswählen, dann "Cockpit laden" klicken.</div>
            </div>
          </v-tab-item>
          <!-- ==================== CSV-IMPORT TAB ==================== -->
          <v-tab-item>
            <v-card outlined class="mb-4" dark>
              <v-card-title class="d-flex align-center">
                <v-icon left>mdi-email-sync-outline</v-icon>
                CSV-Import aus Mailbox
                <v-spacer />
                <v-btn-toggle v-model="csvImportStatusFilter" mandatory dense dark color="primary">
                  <v-btn small value="pending">Pending</v-btn>
                  <v-btn small value="imported">Importiert</v-btn>
                  <v-btn small value="rejected">Verworfen</v-btn>
                </v-btn-toggle>
                <v-btn small color="primary" depressed class="ml-3" @click="triggerCsvSync" :loading="csvSyncRunning">
                  <v-icon left small>mdi-cloud-sync-outline</v-icon> Mailbox jetzt prüfen
                </v-btn>
                <v-btn small color="success" depressed class="ml-2" @click="bulkConfirmReady" :disabled="readyForBulkImport.length === 0">
                  <v-icon left small>mdi-check-all</v-icon>
                  Alle eindeutigen importieren ({{ readyForBulkImport.length }})
                </v-btn>
              </v-card-title>

              <v-card-text>
                <v-alert type="info" dense text outlined class="mb-3">
                  Hier landen alle CSV-Anhänge aus Mails an <code>app.controlling@wellcomefit.ch</code>. Der Sync läuft per Cronjob; du kannst ihn
                  aber auch manuell anstossen.
                </v-alert>

                <v-data-table
                  :headers="csvImportHeaders"
                  :items="csvImports"
                  :loading="csvImportsLoading"
                  dense
                  item-key="_id"
                  class="transparent-table"
                  :items-per-page="25">
                  <template v-slot:item.emailDate="{ item }">
                    {{ $moment(item.emailDate).format("DD.MM.YYYY HH:mm") }}
                  </template>

                  <template v-slot:item.originalFilename="{ item }">
                    <span :title="item.originalFilename">{{ item.originalFilename }}</span>
                    <v-chip v-if="item.mappingSource === 'manual'" x-small color="info" class="ml-1">manuell</v-chip>
                  </template>

                  <template v-slot:item.studio="{ item }">
                    <v-chip v-if="item.studioId" small color="success" outlined>
                      {{ item.studioId.name }}
                    </v-chip>
                    <v-chip v-else small color="warning" outlined>
                      <v-icon left x-small>mdi-help-circle-outline</v-icon>
                      Nicht gemappt
                    </v-chip>
                  </template>

                  <template v-slot:item.period="{ item }"> {{ monthLabelMap[item.month] }} {{ item.year }} </template>

                  <template v-slot:item.total="{ item }">
                    <strong>{{ formatMoney(getCsvImportTotal(item)) }}</strong>
                    <v-icon
                      v-if="item.hasExistingValue"
                      small
                      color="warning"
                      class="ml-1"
                      :title="`Bereits Werte vorhanden: ${formatMoney(item.existingTotal)}`">
                      mdi-alert-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex justify-end" v-if="item.status === 'pending'">
                      <v-btn icon small @click="openCsvImportDetail(item)" title="Details / Bearbeiten">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon small color="success" @click="confirmCsvImport(item)" :disabled="!item.studioId" title="Importieren">
                        <v-icon small>mdi-check</v-icon>
                      </v-btn>
                      <v-btn icon small color="error" @click="rejectCsvImport(item)" title="Verwerfen">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <div v-else class="grey--text caption">
                      <span v-if="item.status === 'imported'">
                        <v-icon x-small color="success">mdi-check-circle</v-icon>
                        {{ $moment(item.importedAt).format("DD.MM HH:mm") }}
                      </span>
                      <span v-if="item.status === 'rejected'">
                        <v-icon x-small color="error">mdi-close-circle</v-icon>
                        verworfen
                      </span>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

            <!-- DETAIL/EDIT DIALOG -->
            <v-dialog v-model="csvDetailDialog" max-width="800px" scrollable>
              <v-card dark v-if="csvDetailItem">
                <v-card-title>
                  CSV-Import Details
                  <v-spacer />
                  <v-btn icon @click="csvDetailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle>
                  {{ csvDetailItem.originalFilename }}
                  <span class="grey--text">• Mail vom {{ $moment(csvDetailItem.emailDate).format("DD.MM.YYYY HH:mm") }}</span>
                </v-card-subtitle>

                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="csvDetailItem.studioId"
                        :items="mappedStudios"
                        item-text="name"
                        item-value="_id"
                        label="Studio zuweisen"
                        outlined
                        dense
                        dark
                        clearable
                        :return-object="false"></v-select>
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-select
                        v-model="csvDetailItem.month"
                        :items="months"
                        item-text="label"
                        item-value="key"
                        label="Monat"
                        outlined
                        dense
                        dark></v-select>
                    </v-col>
                    <v-col cols="6" md="3">
                      <v-select v-model="csvDetailItem.year" :items="availableYears" label="Jahr" outlined dense dark></v-select>
                    </v-col>
                  </v-row>

                  <v-alert v-if="csvDetailItem.hasExistingValue" type="warning" dense text outlined>
                    <strong>Achtung:</strong> Für {{ monthLabelMap[csvDetailItem.month] }} {{ csvDetailItem.year }} sind bereits Werte hinterlegt
                    (Total {{ formatMoney(csvDetailItem.existingTotal) }}). Beim Import werden diese überschrieben.
                  </v-alert>

                  <v-divider class="my-3" />

                  <div class="text-subtitle-2 mb-2">Geparste Werte</div>
                  <v-simple-table dense dark class="transparent-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Position</th>
                          <th class="text-right" style="width: 200px">Betrag</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="item in revenueItems" :key="item.key">
                          <td>{{ item.label }}</td>
                          <td>
                            <v-text-field
                              v-model.number="csvDetailItem.parsedRevenue[item.key]"
                              type="number"
                              dense
                              hide-details
                              outlined
                              dark
                              class="compact-input my-1"></v-text-field>
                          </td>
                        </tr>
                        <tr class="font-weight-bold">
                          <td>TOTAL</td>
                          <td class="text-right pr-3">{{ formatMoney(getCsvImportTotal(csvDetailItem)) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                  <v-expansion-panels flat class="mt-3">
                    <v-expansion-panel>
                      <v-expansion-panel-header class="text-caption">Original-CSV anzeigen</v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <pre style="max-height: 250px; overflow: auto; font-size: 11px">{{ csvDetailItem.rawCsv }}</pre>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>

                <v-card-actions>
                  <v-btn color="error" text @click="rejectCsvImport(csvDetailItem)">Verwerfen</v-btn>
                  <v-spacer />
                  <v-btn text @click="csvDetailDialog = false">Abbrechen</v-btn>
                  <v-btn color="primary" depressed @click="saveCsvDetail">Änderungen speichern</v-btn>
                  <v-btn color="success" depressed @click="saveAndConfirmCsvDetail" :disabled="!csvDetailItem.studioId">
                    Speichern & Importieren
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
        <document-dialog
          v-if="unmappedDocumentDialog"
          :dialog="unmappedDocumentDialog"
          :documentData="unmappedDocumentForm"
          :invoiceIssuerOptions="unmappedInvoiceIssuerOptions"
          :selectedInvoiceIssuer="unmappedSelectedInvoiceIssuer"
          :issuerSearch="''"
          :menu="false"
          hide-description
          @save-document="saveUnmappedDocument"
          @close-document="unmappedDocumentDialog = false"
          @create-new-invoice-issuer="createUnmappedInvoiceIssuer" />
        <!-- PREVIEW DIALOG -->
        <v-dialog v-model="previewDialog" max-width="900px" @click:outside="closePreview">
          <v-card dark>
            <v-card-title class="d-flex align-center">
              Dokumenten-Vorschau
              <v-spacer />
              <v-btn icon @click="closePreview"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text class="pa-0" style="height: 75vh; overflow: hidden">
              <iframe v-if="previewUrl" :src="previewUrl" width="100%" height="100%" style="border: none"></iframe>
              <div v-else class="d-flex justify-center align-center fill-height">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
        <v-dialog v-model="mappingDialog" max-width="780px" scrollable>
          <v-card dark>
            <v-card-title class="d-flex align-center">
              <v-icon color="info" class="mr-2">mdi-map-marker-path</v-icon>
              Kategorie-Mapping
              <v-spacer />
              <v-btn icon @click="mappingDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-subtitle class="pt-2">
              Belege werden anhand ihrer <strong>Kategorie</strong> (gesetzt im Dokumenten-Dialog) automatisch einer Kosten-Position im Controlling
              zugeordnet. Belege ohne Kategorie oder mit einer Kategorie, die hier nicht aufgeführt ist, landen in der Liste der nicht zugeordneten
              Belege und müssen manuell ergänzt werden.
            </v-card-subtitle>

            <v-card-text style="max-height: 60vh">
              <v-simple-table dense dark class="transparent-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left" style="width: 35%">Kosten-Position (Controlling)</th>
                      <th class="text-left">Akzeptierte Beleg-Kategorien</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="group in groupedCategoryMapping" :key="group.costKey">
                      <td class="font-weight-bold primary--text text--lighten-2">
                        {{ group.label }}
                      </td>
                      <td>
                        <v-chip v-for="cat in group.sources" :key="cat" x-small class="ma-1" color="grey darken-3">
                          {{ cat }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <v-divider class="my-4" />

              <div v-if="unmappedCategoriesInData.length">
                <div class="text-subtitle-2 warning--text mb-2">
                  <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                  Aktuell nicht gemappte Kategorien in deinen Belegen:
                </div>
                <v-chip v-for="cat in unmappedCategoriesInData" :key="cat" small class="ma-1" color="warning" outlined>
                  {{ cat }}
                </v-chip>
                <div class="caption grey--text mt-2">
                  Diese Kategorien existieren in der Dokumenten-Ablage, sind aber im Mapping nicht hinterlegt. Entweder die Belege auf eine bestehende
                  Kategorie umstellen oder das Mapping im Backend (<code>yearlyPlanBackendRoutes.js</code>) erweitern.
                </div>
              </div>
              <div v-else class="caption grey--text">
                <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                Alle in deinen Belegen verwendeten Kategorien sind im Mapping abgedeckt.
              </div>
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn color="primary" text @click="mappingDialog = false">Schliessen</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- MANUAL REVENUE DIALOG -->
        <v-dialog v-model="manualRevenueDialog.isOpen" max-width="650px" scrollable>
          <v-card dark class="glass-card">
            <v-card-title class="primary white--text font-weight-bold d-flex align-center">
              <v-icon left class="mr-2">mdi-cash-plus</v-icon>
              Umsatz hinzufügen: {{ manualRevenueDialog.categoryLabel }} ({{ manualRevenueDialog.monthLabel }})
              <v-spacer />
              <v-btn icon @click="manualRevenueDialog.isOpen = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text class="py-4">
              <!-- ADD / EDIT FORM -->
              <v-card outlined class="mb-4 pa-4 secondary-bg" dark>
                <div class="subtitle-2 mb-3 primary--text font-weight-bold">
                  {{ manualRevenueDialog.editId ? "Umsatzeintrag bearbeiten" : "Neuen Umsatz erfassen" }}
                </div>
                <v-form ref="manualRevenueForm" v-model="manualRevenueFormValid">
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-select
                        v-model="manualRevenueDialog.form.studioId"
                        :items="manualRevenueActiveStudios"
                        item-text="name"
                        item-value="_id"
                        label="Studio"
                        outlined
                        dense
                        required
                        :rules="[(v) => !!v || 'Studio ist erforderlich']"
                        dark></v-select>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="manualRevenueDialog.form.amount"
                        label="Umsatz (CHF)"
                        type="number"
                        outlined
                        dense
                        required
                        :rules="[(v) => !!v || 'Wert ist erforderlich', (v) => v > 0 || 'Wert muss grösser als 0 sein']"
                        dark></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        v-model="manualRevenueDialog.form.title"
                        label="Titel / Beschreibung (z.B. Squash Sponsoring)"
                        outlined
                        dense
                        required
                        :rules="[(v) => !!v || 'Titel ist erforderlich']"
                        dark></v-text-field>
                    </v-col>
                  </v-row>
                  <div class="d-flex justify-end mt-2">
                    <v-btn v-if="manualRevenueDialog.editId" text small color="grey" class="mr-2" @click="resetManualRevenueForm"> Abbrechen </v-btn>
                    <v-btn
                      color="success"
                      depressed
                      small
                      :disabled="!manualRevenueFormValid"
                      @click="saveManualRevenueEntry"
                      :loading="manualRevenueDialog.isSaving">
                      <v-icon left small>mdi-content-save</v-icon>
                      {{ manualRevenueDialog.editId ? "Speichern" : "Hinzufügen" }}
                    </v-btn>
                  </div>
                </v-form>
              </v-card>

              <!-- EXISTING ENTRIES LIST -->
              <div class="subtitle-1 mb-2 font-weight-bold d-flex align-center">
                <v-icon left small color="grey" class="mr-2">mdi-format-list-bulleted</v-icon>
                Erfasste Umsätze ({{ manualRevenueDialog.entries.length }})
              </div>
              <v-divider class="mb-3"></v-divider>

              <v-progress-linear v-if="manualRevenueDialog.isLoading" indeterminate color="primary" class="mb-3"></v-progress-linear>

              <div v-else-if="manualRevenueDialog.entries.length === 0" class="text-center py-6 grey--text">
                Keine manuellen Umsätze für diesen Bereich erfasst.
              </div>

              <v-list v-else dense dark class="transparent">
                <v-list-item v-for="entry in manualRevenueDialog.entries" :key="entry._id" class="mb-2 list-item-card" outlined>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold subtitle-2 white--text">
                      {{ entry.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="grey--text text--lighten-1">
                      {{ entry.studioId ? entry.studioId.name : "Unbekanntes Studio" }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="flex-row align-center">
                    <span class="mr-4 font-weight-black success--text subtitle-1"> CHF {{ formatNumber(entry.amount) }} </span>
                    <v-btn icon small color="primary" @click="editManualRevenueEntry(entry)" title="Bearbeiten">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="deleteManualRevenueEntry(entry._id)"
                      :loading="manualRevenueDialog.isDeleting === entry._id"
                      title="Löschen">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- COST DETAIL DIALOG (INVOICES / EXPENSES BREAKDOWN) -->
        <v-dialog v-model="costDetailDialog.isOpen" max-width="850px" scrollable>
          <v-card dark class="glass-card">
            <v-card-title class="error darken-3 white--text font-weight-bold d-flex align-center">
              <v-icon left class="mr-2">mdi-receipt</v-icon>
              Kosten-Aufschlüsselung: {{ costDetailDialog.categoryLabel }} ({{ costDetailDialog.monthLabel }})
              <v-chip class="ml-3 font-weight-black" small color="white" text-color="error">
                Total: CHF {{ formatMoney(sumExpenseAmounts(costDetailDialog.expenses)) }}
              </v-chip>
              <v-spacer />
              <v-btn icon @click="costDetailDialog.isOpen = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-card-text class="py-4">
              <v-progress-linear v-if="costDetailDialog.isLoading" indeterminate color="error" class="mb-4"></v-progress-linear>

              <div v-else-if="Object.keys(costDetailDialog.groupedExpenses).length === 0" class="text-center py-10 grey--text">
                <v-icon size="64" color="grey" class="mb-3">mdi-folder-open-outline</v-icon>
                <div class="subtitle-1">Keine Rechnungen oder Ausgaben für diesen Monat gefunden.</div>
              </div>

              <div v-else>
                <v-expansion-panels v-model="costDetailDialog.activePanels" multiple class="modern-panels">
                  <v-expansion-panel v-for="(expenses, studioName) in costDetailDialog.groupedExpenses" :key="studioName" class="mb-3 panel-card">
                    <v-expansion-panel-header class="font-weight-bold py-3 subtitle-1">
                      <span class="d-flex align-center">
                        <v-icon color="error" class="mr-2">mdi-office-building</v-icon>
                        {{ studioName }}
                        <v-chip class="ml-3" small color="error" text-color="white"> {{ expenses.length }} Belege </v-chip>
                      </span>
                      <template v-slot:actions>
                        <span class="mr-3 font-weight-black error--text text--lighten-1"> CHF {{ formatNumber(sumExpenseAmounts(expenses)) }} </span>
                        <v-icon color="grey">mdi-chevron-down</v-icon>
                      </template>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <v-simple-table dense dark class="detail-table">
                        <template v-slot:default>
                          <thead>
                            <tr>
                              <th class="text-left py-2 font-weight-bold grey--text">Datum</th>
                              <th class="text-left py-2 font-weight-bold grey--text">Bezeichnung / Empfänger</th>
                              <th class="text-left py-2 font-weight-bold grey--text">Beleg-Nr.</th>
                              <th class="text-left py-2 font-weight-bold grey--text">Typ</th>
                              <th class="text-right py-2 font-weight-bold grey--text">Betrag</th>
                              <th class="text-right py-2 font-weight-bold grey--text" style="width: 50px"></th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="exp in expenses" :key="exp._id" class="detail-row">
                              <td class="text-left py-2">{{ $moment(exp.date).format("DD.MM.YYYY") }}</td>
                              <td class="text-left py-2">
                                <span class="font-weight-medium">{{ exp.title }}</span>
                                <div v-if="exp.invoiceIssuer" class="caption grey--text">{{ exp.invoiceIssuer }}</div>
                              </td>
                              <td class="text-left py-2 text-caption font-mono grey--text text--lighten-1">
                                {{ exp.invoiceNumber || "—" }}
                              </td>
                              <td class="text-left py-2">
                                <v-chip small :color="getSourceColor(exp.source)" text-color="white" style="font-size: 10px; height: 18px">
                                  {{ getSourceLabel(exp.source) }}
                                </v-chip>
                              </td>
                              <td class="text-right py-2 font-weight-bold error--text text--lighten-2">CHF {{ formatNumber(exp.amount) }}</td>
                              <td class="text-right py-2">
                                <v-btn
                                  v-if="exp.source === 'document' || exp.source === 'creditCardBill'"
                                  icon
                                  small
                                  color="cyan"
                                  @click="previewCostDocument(exp)"
                                  title="PDF Vorschau">
                                  <v-icon small>mdi-eye</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import { mapState, mapActions, mapGetters } from "vuex"
import DocumentDialog from "@/views/dashboard/documents/components/documentDialog.vue"

function getDefaultMonthly() {
  return { jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0, jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dec: 0 }
}

function getDefaultPlan() {
  return {
    studioId: null,
    year: "2026",
    startMonth: "jan", // NEU
    members: {
      baseJan: 0,
      joined: getDefaultMonthly(),
      left: getDefaultMonthly(),
      pausedRate: getDefaultMonthly(), // Now it is percentage based (e.g. 5 means 5%)
    },
    revenue: {
      dues: 0,
      startupFees: 0,
      bistro: 0,
      medical: 0,
      nutrition: 0,
      shop: 0,
      solarium: 0,
      childcare: 0,
      squash: 0,
      physio: 0,
      massage: 0,
      rental: 0,
      ads: 0,
      debtCollection: 0,
      solarPower: 0,
      other: 0,
      growthRates: {
        dues: 0,
        startupFees: 0,
        bistro: 0,
        medical: 0,
        nutrition: 0,
        shop: 0,
        solarium: 0,
        childcare: 0,
        squash: 0,
        physio: 0,
        massage: 0,
        rental: 0,
        ads: 0,
        debtCollection: 0,
        solarPower: 0,
        other: 0,
      },
      extraMonthly: {
        dues: getDefaultMonthly(),
        startupFees: getDefaultMonthly(),
        bistro: getDefaultMonthly(),
        medical: getDefaultMonthly(),
        nutrition: getDefaultMonthly(),
        shop: getDefaultMonthly(),
        solarium: getDefaultMonthly(),
        childcare: getDefaultMonthly(),
        squash: getDefaultMonthly(),
        physio: getDefaultMonthly(),
        massage: getDefaultMonthly(),
        rental: getDefaultMonthly(),
        ads: getDefaultMonthly(),
        debtCollection: getDefaultMonthly(),
        solarPower: getDefaultMonthly(),
        other: getDefaultMonthly(),
      },
    },
    costs: {
      cogs: 0,
      personnel: { thirdParty: 0, wages: 0 },
      rent: 0,
      energy: 0,
      insuranceFee: 0,
      vehicles: 0,
      marketing: 0,
      travel: 0,
      maintenance: 0,
      admin: 0,
      it: 0,
      consulting: 0,
      licenses: 0,
      leasing: 0,
      otherOps: 0,
      badDebt: 0,
      depreciation: 0,
      financeIncome: 0,
      financeCost: 0,
      neutralCost: 0,
      neutralIncome: 0,
      taxes: 0,
      growthRates: {
        cogs: 0,
        personnel: { thirdParty: 0, wages: 0 },
        rent: 0,
        energy: 0,
        insuranceFee: 0,
        vehicles: 0,
        marketing: 0,
        travel: 0,
        maintenance: 0,
        admin: 0,
        it: 0,
        consulting: 0,
        licenses: 0,
        leasing: 0,
        otherOps: 0,
        badDebt: 0,
        depreciation: 0,
        financeIncome: 0,
        financeCost: 0,
        neutralCost: 0,
        neutralIncome: 0,
        taxes: 0,
      },
      extraMonthly: {
        cogs: getDefaultMonthly(),
        personnel: { thirdParty: getDefaultMonthly(), wages: getDefaultMonthly() },
        rent: getDefaultMonthly(),
        energy: getDefaultMonthly(),
        insuranceFee: getDefaultMonthly(),
        vehicles: getDefaultMonthly(),
        marketing: getDefaultMonthly(),
        travel: getDefaultMonthly(),
        maintenance: getDefaultMonthly(),
        admin: getDefaultMonthly(),
        it: getDefaultMonthly(),
        consulting: getDefaultMonthly(),
        licenses: getDefaultMonthly(),
        leasing: getDefaultMonthly(),
        otherOps: getDefaultMonthly(),
        badDebt: getDefaultMonthly(),
        depreciation: getDefaultMonthly(),
        financeIncome: getDefaultMonthly(),
        financeCost: getDefaultMonthly(),
        neutralCost: getDefaultMonthly(),
        neutralIncome: getDefaultMonthly(),
        taxes: getDefaultMonthly(),
      },
    },
  }
}

export default {
  name: "PlanningMain",
  components: {
    DocumentDialog,
  },
  data() {
    return {
      mappingDialog: false,
      categoryMapping: {
        Handelswaren: "cogs",
        Personalaufwand: "wages",
        "Leistungen Dritter": "thirdParty",
        RAUMAUFWAND: "rent",
        "ENERGIE- UND ENTSORGUNGSAUFWAND": "energy",
        "SACHVERSICH., ABGABEN, GEBÜHREN, BEWILLIGUNGEN": "insuranceFee",
        "FAHRZEUG- UND TRANSPORTAUFWAND": "vehicles",
        Fahrzeuge: "vehicles",
        WERBEAUFWAND: "marketing",
        "Reisespesen/Kundenbetreuung": "travel",
        "Reparaturen / Wartungen": "maintenance",
        "VERWALTUNGS-AUFWAND": "admin",
        INFORMATIKAUFWAND: "it",
        Beratungskosten: "consulting",
        Lizenzen: "licenses",
        "Leasing Maschinen": "leasing",
        "ÜBRIGER BETRIEBSAUFWAND": "otherOps",
        Kreditkarten: "otherOps",
        Forderungsverluste: "badDebt",
        Steuern: "taxes",
        "Fitnessgeräte und Solarien": "depreciation",
        "Sauna / Dampfbad": "depreciation",
        "Mobilien und Einrichtungen": "depreciation",
        "Büromobiliar/Büro-/EDV-Geräte": "depreciation",
      },

      unmappedTab: 0,
      unmappedHeaders: [
        { text: "Datum", value: "date" },
        { text: "Titel", value: "title" },
        { text: "Rechnungsnr.", value: "invoiceNumber" },
        { text: "Kategorie", value: "category" },
        { text: "Betrag", value: "amount", align: "end" },
        { text: "Quelle", value: "source" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],

      unmappedDocumentDialog: false,
      unmappedDocumentForm: {
        _id: null,
        title: "",
        description: "",
        category: null,
        dueDate: null,
        studio: null,
        invoiceIssuer: null,
        amount: null,
        invoiceNumber: "",
        date: null,
      },
      unmappedSelectedInvoiceIssuer: null,
      unmappedInvoiceIssuerOptions: [],

      activeTab: 0,
      viewMode: "input",
      isEditing: false,
      isSaving: false,

      availableYears: ["2025", "2026", "2027", "2028"],

      months: [
        { key: "jan", label: "Jan" },
        { key: "feb", label: "Feb" },
        { key: "mar", label: "Mrz" },
        { key: "apr", label: "Apr" },
        { key: "may", label: "Mai" },
        { key: "jun", label: "Jun" },
        { key: "jul", label: "Jul" },
        { key: "aug", label: "Aug" },
        { key: "sep", label: "Sep" },
        { key: "oct", label: "Okt" },
        { key: "nov", label: "Nov" },
        { key: "dec", label: "Dez" },
      ],

      // Definition der dynamischen Felder für die Umsatz-Tabelle
      revenueItems: [
        { key: "dues", label: "Mitgliedsbeiträge" },
        { key: "startupFees", label: "Startpakete / Gebühren" },
        { key: "bistro", label: "Bistro" },
        { key: "medical", label: "Kurse / Tests / Arzt" },
        { key: "nutrition", label: "VK Ernährung" },
        { key: "shop", label: "VK Shop" },
        { key: "solarium", label: "Solarium" },
        { key: "childcare", label: "Kinderhort" },
        { key: "squash", label: "Squash" },
        { key: "physio", label: "Physiotherapie" },
        { key: "massage", label: "Entspannung / Massage" },
        { key: "rental", label: "Vermietung" },
        { key: "ads", label: "TV / Werbung" },
        { key: "debtCollection", label: "Ertrag Betreibungskosten" },
        { key: "solarPower", label: "PV-Strom Ertrag" },
        { key: "other", label: "Übriger Ertrag" },
      ],

      // Definition der dynamischen Felder für die Kosten-Tabelle
      costItems: [
        { key: "cogs", label: "Wareneinsatz" },
        { key: "wages", label: "Personalaufwand", isNested: "personnel", nestedKey: "wages" },
        { key: "thirdParty", label: "Leistungen Dritter", isNested: "personnel", nestedKey: "thirdParty" },
        { key: "rent", label: "Raumaufwand" },
        { key: "energy", label: "Energie- und Entsorgungsaufwand" },
        { key: "insuranceFee", label: "Sachversicherungen, Abgaben" },
        { key: "vehicles", label: "Fahrzeug- und Transportaufwand" },
        { key: "marketing", label: "Werbeaufwand" },
        { key: "travel", label: "Reisespesen / Kundenbetreuung" },
        { key: "maintenance", label: "Reparaturen / Wartungen" },
        { key: "admin", label: "Verwaltungsaufwand" },
        { key: "it", label: "Informatikaufwand" },
        { key: "consulting", label: "Beratungskosten" },
        { key: "licenses", label: "Lizenzen" },
        { key: "leasing", label: "Leasing" },
        { key: "otherOps", label: "Übriger Betriebsaufwand" },
        { key: "badDebt", label: "Forderungsverluste" },
        { key: "depreciation", label: "Abschreibungen" },
        { key: "financeCost", label: "Finanzaufwand" },
        { key: "financeIncome", label: "Finanzerträge" },
        { key: "neutralCost", label: "Neutraler Aufwand" },
        { key: "neutralIncome", label: "Neutraler Ertrag" },
        { key: "taxes", label: "Betriebliche Steuern" },
      ],

      planData: getDefaultPlan(),
      actualData: null,

      // Controlling Tab State
      controllingYear: "2026",
      controllingStudioIds: [],
      isControllingLoading: false,
      controllingCalculatedPlanObj: null,
      controllingCalculatedActualsObj: null,

      // Cockpit Tab State
      cockpitYear: "2026",
      cockpitStudioIds: [],
      cockpitLoading: false,
      cockpitPlanAgg: null,
      cockpitActualsAgg: null,
      allStudiosPlanCalcs: [], // all 26 studios for benchmarking

      editMonthlyDialog: {
        isOpen: false,
        key: null,
        isNested: null,
        nestedKey: null,
        type: null, // "revenue" or "costs"
        label: "",
      },
      // CSV-Import Tab State
      csvImports: [],
      csvImportsLoading: false,
      csvSyncRunning: false,
      csvImportStatusFilter: "pending",
      csvDetailDialog: false,
      csvDetailItem: null,
      csvImportHeaders: [
        { text: "Mail-Datum", value: "emailDate" },
        { text: "Datei", value: "originalFilename" },
        { text: "Studio", value: "studio", sortable: false },
        { text: "Periode", value: "period", sortable: false },
        { text: "Total", value: "total", align: "end", sortable: false },
        { text: "Aktionen", value: "actions", align: "end", sortable: false },
      ],
      monthLabelMap: {
        jan: "Jan",
        feb: "Feb",
        mar: "Mrz",
        apr: "Apr",
        may: "Mai",
        jun: "Jun",
        jul: "Jul",
        aug: "Aug",
        sep: "Sep",
        oct: "Okt",
        nov: "Nov",
        dec: "Dez",
      },
      previewDialog: false,
      previewUrl: null,

      // NEW CONTROLLING ENHANCEMENTS STATE
      manualRevenueDialog: {
        isOpen: false,
        isLoading: false,
        isSaving: false,
        isDeleting: null,
        categoryKey: "",
        categoryLabel: "",
        monthIndex: 0,
        monthKey: "",
        monthLabel: "",
        entries: [],
        editId: null,
        form: {
          studioId: "",
          amount: "",
          title: "",
        },
      },
      manualRevenueFormValid: false,

      costDetailDialog: {
        isOpen: false,
        isLoading: false,
        categoryKey: "",
        categoryLabel: "",
        monthIndex: 0,
        monthLabel: "",
        expenses: [],
        groupedExpenses: {},
        activePanels: [],
      },
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    pendingImportsCount() {
      return this.csvImports.filter((i) => i.status === "pending").length
    },
    readyForBulkImport() {
      // Eindeutig gemappt + kein Konflikt mit existierenden Werten
      return this.csvImports.filter((i) => i.status === "pending" && i.studioId && !i.hasExistingValue)
    },
    groupedCategoryMapping() {
      const labelByKey = {}
      this.costItems.forEach((c) => (labelByKey[c.key] = c.label))

      const groups = {}
      Object.entries(this.categoryMapping).forEach(([docCategory, costKey]) => {
        if (!groups[costKey]) {
          groups[costKey] = { costKey, label: labelByKey[costKey] || costKey, sources: [] }
        }
        groups[costKey].sources.push(docCategory)
      })

      // alphabetisch nach Ziel-Label sortieren
      return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label))
    },
    unmappedCategoriesInData() {
      // Welche Doku-Kategorien tauchen aktuell als "unknownCategory" auf?
      const set = new Set()
      ;(this.unmappedView.unknownCategory || []).forEach((item) => {
        if (item.category) set.add(item.category)
      })
      return Array.from(set).sort()
    },
    activeActuals() {
      return this.controllingCalculatedActualsObj
    },
    unmappedView() {
      // actualData ist je nach Studio-Auswahl entweder ein Objekt oder ein Array.
      // Hier am Beispiel eines einzelnen aktiven Studios:
      return this.activeActuals?.unmapped || { noCategory: [], unknownCategory: [], invalid: [] }
    },
    currentUnmappedList() {
      return [this.unmappedView.noCategory, this.unmappedView.unknownCategory, this.unmappedView.invalid][this.unmappedTab]
    },
    hasUnmappedDocuments() {
      const u = this.unmappedView
      return u.noCategory.length + u.unknownCategory.length + u.invalid.length > 0
    },
    totalUnmappedCount() {
      const u = this.unmappedView
      return u.noCategory.length + u.unknownCategory.length + u.invalid.length
    },
    totalUnmappedAmount() {
      const t = this.activeActuals?.unmappedTotals || {}
      return (t.noCategory || 0) + (t.unknownCategory || 0) + (t.invalid || 0)
    },
    calculatedActuals() {
      return this.computeCalculatedActuals(this.actualData)
    },
    calculatedData() {
      return this.computeCalculatedData(this.planData)
    },
    opexItems() {
      const nonOpex = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]
      return this.costItems.filter((c) => !nonOpex.includes(c.key))
    },
    nonOpexItems() {
      const list = ["financeIncome", "financeCost", "neutralCost", "neutralIncome", "taxes"]
      return this.costItems.filter((c) => list.includes(c.key))
    },

    mappedStudios() {
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => {
          return { _id: studio._id, name: studio.name }
        })
    },
    manualRevenueActiveStudios() {
      if (this.controllingStudioIds && this.controllingStudioIds.length > 0) {
        return this.mappedStudios.filter((s) => this.controllingStudioIds.includes(s._id))
      }
      return this.mappedStudios
    },

    // ===== COCKPIT CHART OPTIONS & SERIES =====
    monthLabels() {
      return this.months.map((m) => m.label)
    },

    // IST helpers: monthly totals derived from cockpitActualsAgg
    istMonthlyRevenue() {
      if (!this.cockpitActualsAgg) return new Array(12).fill(0)
      return this.months.map((m, i) => this.revenueItems.reduce((sum, item) => sum + (this.cockpitActualsAgg.revenue[item.key]?.[i] || 0), 0))
    },
    istYearlyRevenue() {
      return this.istMonthlyRevenue.reduce((s, v) => s + v, 0)
    },
    istMonthlyEbitda() {
      if (!this.cockpitActualsAgg) return new Array(12).fill(0)
      return this.months.map((m, i) => {
        const rev = this.istMonthlyRevenue[i]
        const cogs = this.cockpitActualsAgg.costs?.cogs?.[i] || 0
        const opex = this.cockpitActualsAgg.kpi.gesamtkosten[i] || 0
        return rev - cogs - opex
      })
    },
    istYearlyEbitda() {
      return this.istMonthlyEbitda.reduce((s, v) => s + v, 0)
    },
    istYearlyJoined() {
      if (!this.cockpitActualsAgg?.members?.joined) return 0
      return this.cockpitActualsAgg.members.joined.reduce((s, v) => s + (v || 0), 0)
    },
    istYearlyLeft() {
      if (!this.cockpitActualsAgg?.members?.left) return 0
      return this.cockpitActualsAgg.members.left.reduce((s, v) => s + (v || 0), 0)
    },
    hasIst() {
      // IST data is considered "present" if any revenue value is non-zero
      return this.istYearlyRevenue > 0 || this.istYearlyJoined > 0
    },

    // --- Big Four Chart Series ---
    membersChartSeries() {
      if (!this.cockpitPlanAgg) return []
      const planEnd = this.cockpitPlanAgg.members.map((m) => Math.round(m.end))
      const istEnd = (this.cockpitActualsAgg?.members?.end || new Array(12).fill(0)).map((v) => Math.round(v || 0))
      return [
        { name: "Plan (Monatsende)", data: planEnd },
        { name: "IST (Monatsende)", data: istEnd },
      ]
    },
    membersChartOptions() {
      return this.buildBarChartOptions("Mitglieder", ["#455a64", "#4caf50"], false)
    },

    joinLeftChartSeries() {
      if (!this.cockpitPlanAgg) return []
      const series = [
        { name: "Zugänge (Plan)", data: this.cockpitPlanAgg.members.map((m) => Math.round(m.joined)) },
        { name: "Abgänge (Plan)", data: this.cockpitPlanAgg.members.map((m) => Math.round(m.left)) },
      ]
      if (this.cockpitActualsAgg?.members?.joined) {
        series.push({ name: "Zugänge (IST)", data: this.cockpitActualsAgg.members.joined.map((v) => Math.round(v || 0)) })
        series.push({ name: "Abgänge (IST)", data: this.cockpitActualsAgg.members.left.map((v) => Math.round(v || 0)) })
      }
      return series
    },
    joinLeftChartOptions() {
      return this.buildBarChartOptions("Personen", ["#66bb6a", "#ef5350", "#26a69a", "#e57373"], false)
    },

    ebitdaChartSeries() {
      if (!this.cockpitPlanAgg) return []
      const series = [{ name: "EBITDA (Plan)", data: this.cockpitPlanAgg.kpi.ebitda.map((v) => Math.round(v)) }]
      if (this.cockpitActualsAgg) {
        series.push({ name: "EBITDA (IST)", data: this.istMonthlyEbitda.map((v) => Math.round(v)) })
      }
      return series
    },
    ebitdaChartOptions() {
      return this.buildBarChartOptions("CHF", ["#00bcd4", "#4fc3f7"], true)
    },

    revenueChartSeries() {
      if (!this.cockpitPlanAgg) return []
      const series = [{ name: "Erlöse (Plan)", data: this.cockpitPlanAgg.kpi.gesamtErloese.map((v) => Math.round(v)) }]
      if (this.cockpitActualsAgg) {
        series.push({ name: "Erlöse (IST)", data: this.istMonthlyRevenue.map((v) => Math.round(v)) })
      }
      return series
    },
    revenueChartOptions() {
      return this.buildBarChartOptions("CHF", ["#009688", "#80cbc4"], true)
    },

    // Gauges
    closingRateGaugeSeries() {
      if (!this.cockpitPlanAgg) return [0]
      // Proxy: assume leads = joined * 2.5 (dummy multiplier since no leads in DB)
      // Use IST joined if available, otherwise plan
      const joined = this.hasIst ? this.istYearlyJoined : this.cockpitPlanAgg.kpi.yearlyJoined || 0
      const dummyLeads = joined ? joined * 2.5 : 1
      const rate = Math.min(Math.round((joined / dummyLeads) * 100), 100)
      return [rate]
    },
    closingRateGaugeOptions() {
      return this.buildGaugeOptions(this.hasIst ? "IST Abschlussquote" : "Plan Abschlussquote", "#4caf50")
    },
    fluctuationGaugeSeries() {
      if (!this.cockpitPlanAgg) return [0]
      // Prefer IST values if available
      const left = this.hasIst ? this.istYearlyLeft : this.cockpitPlanAgg.kpi.yearlyLeft || 0
      const baseJan = this.cockpitPlanAgg.members[0]?.start || 0
      const val = baseJan > 0 ? Math.min(Math.round((left / baseJan) * 100), 100) : 0
      return [val]
    },
    fluctuationGaugeOptions() {
      return this.buildGaugeOptions(this.hasIst ? "IST Fluktuation" : "Plan Fluktuation", "#ef5350")
    },
    growthRateGaugeSeries() {
      if (!this.cockpitPlanAgg) return [0]
      const joined = this.hasIst ? this.istYearlyJoined : this.cockpitPlanAgg.kpi.yearlyJoined || 0
      const left = this.hasIst ? this.istYearlyLeft : this.cockpitPlanAgg.kpi.yearlyLeft || 0
      const baseJan = this.cockpitPlanAgg.members[0]?.start || 0
      const growth = joined - left
      const val = baseJan > 0 ? Math.min(Math.round(Math.abs(growth / baseJan) * 100), 100) : 0
      return [val]
    },
    growthRateGaugeOptions() {
      const joined = this.hasIst ? this.istYearlyJoined : this.cockpitPlanAgg?.kpi.yearlyJoined || 0
      const left = this.hasIst ? this.istYearlyLeft : this.cockpitPlanAgg?.kpi.yearlyLeft || 0
      const isPositive = joined >= left
      return this.buildGaugeOptions(this.hasIst ? "IST Wachstum" : "Plan Wachstum", isPositive ? "#4caf50" : "#ef5350")
    },

    // Benchmarking
    benchmarkRows() {
      if (!this.cockpitPlanAgg || !this.allStudiosPlanCalcs.length) return []

      const plan = this.cockpitPlanAgg
      const all = this.allStudiosPlanCalcs
      const ist = this.hasIst // boolean

      // Helpers across all 26 studio plans (benchmark always uses plan data)
      const avg = (fn) => {
        const vals = all.map(fn).filter((v) => isFinite(v) && v !== 0)
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0
      }
      const best = (fn, higherIsBetter = true) => {
        const vals = all.map(fn).filter((v) => isFinite(v))
        return vals.length ? (higherIsBetter ? Math.max(...vals) : Math.min(...vals)) : 0
      }
      const getAvgMembers = (c) => c.members.reduce((s, m) => s + m.start, 0) / 12 || 1

      // Plan values for selected studios
      const planBaseJan = plan.members[0]?.start || 0
      const planAvgMem = getAvgMembers(plan)

      // Use IST where available, otherwise Plan for the "selected" column
      const selRevenue = ist ? this.istYearlyRevenue : plan.kpi.yearlyTotalRevenue || 0
      const selEbitda = ist ? this.istYearlyEbitda : plan.kpi.yearlyEbitda || 0
      const selJoined = ist ? this.istYearlyJoined : plan.kpi.yearlyJoined || 0
      const selLeft = ist ? this.istYearlyLeft : plan.kpi.yearlyLeft || 0
      const selYearEnd = ist ? this.cockpitActualsAgg?.members?.end?.[11] || plan.members[11]?.end || 0 : plan.members[11]?.end || 0
      const selAvgMem = planAvgMem // use plan member base for per-member calcs
      const selRevPerMember = selRevenue / (selAvgMem || 1)
      const selEbitdaPerMem = selEbitda / (selAvgMem || 1)
      const selFluc = planBaseJan > 0 ? selLeft / planBaseJan : 0
      const selGrowth = selJoined - selLeft
      const suffix = ist ? " (IST)" : " (Plan)"

      const rows = [
        {
          key: "revPerMember",
          label: "Umsatz / Mitglied (p.a.)" + suffix,
          selected: selRevPerMember,
          bestVal: best((c) => c.kpi.yearlyTotalRevenue / getAvgMembers(c)),
          avgVal: avg((c) => c.kpi.yearlyTotalRevenue / getAvgMembers(c)),
          higherIsBetter: true,
          fmt: (v) => "CHF " + this.formatMoney(v),
        },
        {
          key: "ebitdaPerMember",
          label: "EBITDA / Mitglied (p.a.)" + suffix,
          selected: selEbitdaPerMem,
          bestVal: best((c) => c.kpi.yearlyEbitda / getAvgMembers(c)),
          avgVal: avg((c) => c.kpi.yearlyEbitda / getAvgMembers(c)),
          higherIsBetter: true,
          fmt: (v) => "CHF " + this.formatMoney(v),
        },
        {
          key: "yearlyRevenue",
          label: "Gesamterlöse (Jahr)" + suffix,
          selected: selRevenue,
          bestVal: best((c) => c.kpi.yearlyTotalRevenue),
          avgVal: avg((c) => c.kpi.yearlyTotalRevenue),
          higherIsBetter: true,
          fmt: (v) => "CHF " + this.formatMoney(v),
        },
        {
          key: "yearlyEbitda",
          label: "EBITDA (Jahr)" + suffix,
          selected: selEbitda,
          bestVal: best((c) => c.kpi.yearlyEbitda),
          avgVal: avg((c) => c.kpi.yearlyEbitda),
          higherIsBetter: true,
          fmt: (v) => "CHF " + this.formatMoney(v),
        },
        {
          key: "memberYearEnd",
          label: "Mitglieder (Jahresende)",
          selected: selYearEnd,
          bestVal: best((c) => c.members[11]?.end || 0),
          avgVal: avg((c) => c.members[11]?.end || 0),
          higherIsBetter: true,
          fmt: (v) => this.formatMoney(v),
        },
        {
          key: "fluctuation",
          label: "Fluktuationsquote (p.a.)" + suffix,
          selected: selFluc,
          bestVal: best((c) => {
            const b = c.members[0]?.start || 0
            return b > 0 ? c.kpi.yearlyLeft / b : 0
          }, false),
          avgVal: avg((c) => {
            const b = c.members[0]?.start || 0
            return b > 0 ? c.kpi.yearlyLeft / b : 0
          }),
          higherIsBetter: false,
          fmt: (v) => this.formatPercent(v),
        },
        {
          key: "yearlyJoined",
          label: "Zugänge (Jahr)" + suffix,
          selected: selJoined,
          bestVal: best((c) => c.kpi.yearlyJoined),
          avgVal: avg((c) => c.kpi.yearlyJoined),
          higherIsBetter: true,
          fmt: (v) => this.formatMoney(v),
        },
        {
          key: "netGrowth",
          label: "Netto-Wachstum (absolut)" + suffix,
          selected: selGrowth,
          bestVal: best((c) => c.kpi.yearlyGrowth),
          avgVal: avg((c) => c.kpi.yearlyGrowth),
          higherIsBetter: true,
          fmt: (v) => (v >= 0 ? "+" : "") + this.formatMoney(v),
        },
      ]

      return rows.map((r) => {
        const isBetter = r.higherIsBetter ? r.selected >= r.bestVal * 0.9 : r.selected <= r.bestVal * 1.1
        const isWorse = r.higherIsBetter ? r.selected < r.avgVal : r.selected > r.avgVal
        return {
          key: r.key,
          label: r.label,
          selectedFmt: r.fmt(r.selected),
          bestFmt: r.fmt(r.bestVal),
          avgFmt: r.fmt(r.avgVal),
          selectedClass: isBetter ? "text-success" : isWorse ? "text-error" : "",
        }
      })
    },
  },
  methods: {
    async previewUnmappedDocument(item) {
      this.previewUrl = null
      this.previewDialog = true

      try {
        // WICHTIG: Hier gehen wir davon aus, dass du in deiner api.js
        // eine Methode hast, die die Datei als 'blob' zurückgibt.
        const blob = await api.documents.previewDocument(item._id)

        // Erstellt eine sichere, lokale URL aus dem Blob, die der iframe anzeigen kann
        this.previewUrl = URL.createObjectURL(blob)
      } catch (err) {
        console.error("Fehler beim Laden der Vorschau:", err)
        alert("Vorschau konnte nicht geladen werden.")
        this.previewDialog = false
      }
    },

    closePreview() {
      this.previewDialog = false
      // Wichtig für die Performance: Den Speicher des Blobs wieder freigeben!
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
      }
    },
    // ===== NEW CONTROLLING ENHANCEMENTS METHODS =====
    async openManualRevenueModal(item, monthIndex) {
      const monthObj = this.months[monthIndex]
      this.manualRevenueDialog.categoryKey = item.key
      this.manualRevenueDialog.categoryLabel = item.label
      this.manualRevenueDialog.monthIndex = monthIndex
      this.manualRevenueDialog.monthKey = monthObj.key

      const longMonthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]
      this.manualRevenueDialog.monthLabel = longMonthNames[monthIndex]

      this.manualRevenueDialog.isOpen = true
      this.resetManualRevenueForm()
      await this.loadManualRevenues()
    },

    async loadManualRevenues() {
      this.manualRevenueDialog.isLoading = true
      try {
        const params = {
          year: this.controllingYear,
          month: this.manualRevenueDialog.monthKey,
          category: this.manualRevenueDialog.categoryKey,
        }
        const entries = await api.planning.fetchManualRevenues(params)

        // Filter entries to only show those belonging to currently selected controllingStudioIds
        if (this.controllingStudioIds && this.controllingStudioIds.length > 0) {
          this.manualRevenueDialog.entries = entries.filter((e) => {
            let sIdStr = ""
            if (e.studioId) {
              if (typeof e.studioId === "object") {
                sIdStr = String(e.studioId._id || e.studioId)
              } else {
                sIdStr = String(e.studioId)
              }
            }
            return this.controllingStudioIds.includes(sIdStr)
          })
        } else {
          this.manualRevenueDialog.entries = entries
        }
      } catch (err) {
        console.error("Fehler beim Laden der manuellen Umsätze:", err)
      } finally {
        this.manualRevenueDialog.isLoading = false
      }
    },

    resetManualRevenueForm() {
      this.manualRevenueDialog.editId = null
      let defaultStudioId = ""
      if (this.manualRevenueActiveStudios.length > 0) {
        defaultStudioId = this.manualRevenueActiveStudios[0]._id
      }
      this.manualRevenueDialog.form = {
        studioId: defaultStudioId,
        amount: "",
        title: "",
      }
      if (this.$refs.manualRevenueForm) {
        this.$refs.manualRevenueForm.resetValidation()
      }
    },

    async saveManualRevenueEntry() {
      if (!this.$refs.manualRevenueForm.validate()) return
      this.manualRevenueDialog.isSaving = true
      try {
        const payload = {
          studioId: this.manualRevenueDialog.form.studioId,
          year: this.controllingYear,
          month: this.manualRevenueDialog.monthKey,
          category: this.manualRevenueDialog.categoryKey,
          amount: parseFloat(this.manualRevenueDialog.form.amount),
          title: this.manualRevenueDialog.form.title,
        }

        if (this.manualRevenueDialog.editId) {
          await api.planning.updateManualRevenue(this.manualRevenueDialog.editId, payload)
        } else {
          await api.planning.saveManualRevenue(payload)
        }

        this.resetManualRevenueForm()
        await this.loadManualRevenues()
        await this.loadControllingData()
      } catch (err) {
        console.error("Fehler beim Speichern des manuellen Umsatzes:", err)
        alert("Eintrag konnte nicht gespeichert werden.")
      } finally {
        this.manualRevenueDialog.isSaving = false
      }
    },

    editManualRevenueEntry(entry) {
      let sIdStr = ""
      if (entry.studioId) {
        if (typeof entry.studioId === "object") {
          sIdStr = String(entry.studioId._id || entry.studioId)
        } else {
          sIdStr = String(entry.studioId)
        }
      }
      this.manualRevenueDialog.editId = entry._id
      this.manualRevenueDialog.form = {
        studioId: sIdStr,
        amount: entry.amount,
        title: entry.title,
      }
    },

    async deleteManualRevenueEntry(id) {
      if (!confirm("Möchten Sie diesen Eintrag wirklich löschen?")) return
      this.manualRevenueDialog.isDeleting = id
      try {
        await api.planning.deleteManualRevenue(id)
        await this.loadManualRevenues()
        await this.loadControllingData()
      } catch (err) {
        console.error("Fehler beim Löschen des manuellen Umsatzes:", err)
        alert("Eintrag konnte nicht gelöscht werden.")
      } finally {
        this.manualRevenueDialog.isDeleting = null
      }
    },

    // ===== COST DETAILS BREAKDOWN METHODS =====
    async openCostDetailModal(item, monthIndex) {
      const longMonthNames = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]
      this.costDetailDialog.categoryKey = item.key
      this.costDetailDialog.categoryLabel = item.label
      this.costDetailDialog.monthIndex = monthIndex
      this.costDetailDialog.monthLabel = longMonthNames[monthIndex]
      this.costDetailDialog.isOpen = true
      this.costDetailDialog.expenses = []
      this.costDetailDialog.groupedExpenses = {}
      this.costDetailDialog.activePanels = []

      await this.loadCostDetails()
    },

    async loadCostDetails() {
      this.costDetailDialog.isLoading = true
      try {
        const studioIds = this.controllingStudioIds.length ? this.controllingStudioIds : this.mappedStudios.map((s) => s._id)

        const params = {
          studioIds: studioIds.join(","),
          year: this.controllingYear,
          monthIndex: this.costDetailDialog.monthIndex,
          categoryKey: this.costDetailDialog.categoryKey,
        }

        const expenses = await api.planning.fetchExpensesDetail(params)
        this.costDetailDialog.expenses = expenses

        // Group expenses by studio name
        const grouped = {}
        expenses.forEach((exp) => {
          const sName = exp.studio && exp.studio.name ? exp.studio.name : "Unbekanntes Studio"
          if (!grouped[sName]) {
            grouped[sName] = []
          }
          grouped[sName].push(exp)
        })
        this.costDetailDialog.groupedExpenses = grouped

        // Automatically expand all panels by default
        this.costDetailDialog.activePanels = Object.keys(grouped).map((_, idx) => idx)
      } catch (err) {
        console.error("Fehler beim Laden der Beleg-Details:", err)
      } finally {
        this.costDetailDialog.isLoading = false
      }
    },

    sumExpenseAmounts(expenses) {
      return expenses.reduce((sum, e) => sum + (e.amount || 0), 0)
    },

    getSourceLabel(source) {
      switch (source) {
        case "document":
          return "Beleg"
        case "manualExpense":
          return "Manuell"
        case "recurring":
          return "Dauerauftrag"
        case "creditCardBill":
          return "Kreditkarte"
        default:
          return "Ausgabe"
      }
    },

    getSourceColor(source) {
      switch (source) {
        case "document":
          return "blue"
        case "manualExpense":
          return "orange"
        case "recurring":
          return "purple"
        case "creditCardBill":
          return "cyan darken-2"
        default:
          return "grey"
      }
    },

    async previewCostDocument(exp) {
      this.previewUrl = null
      this.previewDialog = true
      try {
        let blobUrl = ""
        if (exp.source === "creditCardBill") {
          const blob = await api.planning.fetchCreditCardFile(exp._id)
          blobUrl = URL.createObjectURL(blob)
        } else {
          const blob = await api.documents.previewDocument(exp._id)
          blobUrl = URL.createObjectURL(blob)
        }
        this.previewUrl = blobUrl
      } catch (err) {
        console.error("Fehler beim Laden der Beleg-Vorschau:", err)
        alert("Vorschau konnte nicht geladen werden.")
        this.previewDialog = false
      }
    },
    async loadCsvImports() {
      this.csvImportsLoading = true
      try {
        this.csvImports = await api.planning.csvImport.list(this.csvImportStatusFilter)
      } catch (err) {
        console.error("Fehler beim Laden der CSV-Imports:", err)
      } finally {
        this.csvImportsLoading = false
      }
    },
    async triggerCsvSync() {
      this.csvSyncRunning = true
      try {
        const result = await api.planning.csvImport.sync()
        console.log("Sync-Resultat:", result)
        await this.loadCsvImports()
        alert(
          `Sync abgeschlossen: ${result.processed} verarbeitet, ${result.autoMapped} auto-gemappt, ${result.needsReview} brauchen Review, ${result.skipped} übersprungen.`,
        )
      } catch (err) {
        console.error("Sync fehlgeschlagen:", err)
        alert("Sync fehlgeschlagen: " + (err.response?.data?.error || err.message))
      } finally {
        this.csvSyncRunning = false
      }
    },
    getCsvImportTotal(item) {
      if (!item || !item.parsedRevenue) return 0
      return Object.values(item.parsedRevenue).reduce((sum, v) => sum + (v || 0), 0)
    },
    openCsvImportDetail(item) {
      // Deep copy, damit Änderungen nicht direkt durchschlagen bevor gespeichert
      this.csvDetailItem = JSON.parse(JSON.stringify(item))
      // studioId vom populated-Objekt auf reine ID reduzieren für Select
      if (this.csvDetailItem.studioId && typeof this.csvDetailItem.studioId === "object") {
        this.csvDetailItem.studioId = this.csvDetailItem.studioId._id
      }
      this.csvDetailDialog = true
    },
    async saveCsvDetail() {
      try {
        await api.planning.csvImport.update(this.csvDetailItem._id, {
          studioId: this.csvDetailItem.studioId,
          year: this.csvDetailItem.year,
          month: this.csvDetailItem.month,
          parsedRevenue: this.csvDetailItem.parsedRevenue,
        })
        this.csvDetailDialog = false
        await this.loadCsvImports()
      } catch (err) {
        alert("Speichern fehlgeschlagen: " + err.message)
      }
    },
    async saveAndConfirmCsvDetail() {
      try {
        await api.planning.csvImport.update(this.csvDetailItem._id, {
          studioId: this.csvDetailItem.studioId,
          year: this.csvDetailItem.year,
          month: this.csvDetailItem.month,
          parsedRevenue: this.csvDetailItem.parsedRevenue,
        })
        await api.planning.csvImport.confirm(this.csvDetailItem._id)
        this.csvDetailDialog = false
        await this.loadCsvImports()
      } catch (err) {
        alert("Import fehlgeschlagen: " + (err.response?.data?.error || err.message))
      }
    },
    async confirmCsvImport(item) {
      if (item.hasExistingValue) {
        if (
          !confirm(
            `Achtung: Für ${this.monthLabelMap[item.month]} ${item.year} existieren bereits Werte (${this.formatMoney(
              item.existingTotal,
            )}). Wirklich überschreiben?`,
          )
        ) {
          return
        }
      }
      try {
        await api.planning.csvImport.confirm(item._id)
        await this.loadCsvImports()
      } catch (err) {
        alert("Import fehlgeschlagen: " + (err.response?.data?.error || err.message))
      }
    },
    async rejectCsvImport(item) {
      if (!confirm("CSV-Import wirklich verwerfen? Der Eintrag bleibt zur Nachverfolgung erhalten.")) return
      try {
        await api.planning.csvImport.reject(item._id)
        if (this.csvDetailDialog) this.csvDetailDialog = false
        await this.loadCsvImports()
      } catch (err) {
        alert("Verwerfen fehlgeschlagen: " + err.message)
      }
    },
    async bulkConfirmReady() {
      const ids = this.readyForBulkImport.map((i) => i._id)
      if (ids.length === 0) return
      if (!confirm(`${ids.length} CSV-Imports importieren?`)) return
      try {
        const res = await api.planning.csvImport.confirmBulk(ids)
        await this.loadCsvImports()
        alert(`${res.success} importiert, ${res.failed.length} fehlgeschlagen.`)
      } catch (err) {
        alert("Bulk-Import fehlgeschlagen: " + err.message)
      }
    },
    downloadUnmappedDocument(item) {
      const fileName = item.title || "Dokument"
      api.documents.downloadDocument(item._id, fileName)
    },
    async createUnmappedInvoiceIssuer(title) {
      try {
        const createdIssuer = await api.invoiceIssuers.create({ title })
        this.unmappedInvoiceIssuerOptions.push(createdIssuer)
        this.unmappedSelectedInvoiceIssuer = createdIssuer
        this.unmappedDocumentForm.invoiceIssuer = createdIssuer._id
      } catch (err) {
        console.error("Fehler beim Erstellen des neuen Rechnungsstellers:", err)
      }
    },
    async openUnmappedDocument(item) {
      try {
        // entweder fetchen oder direkt das item nehmen + invoiceIssuers laden
        if (!this.unmappedInvoiceIssuerOptions.length) {
          this.unmappedInvoiceIssuerOptions = await api.invoiceIssuers.fetch()
        }
        this.unmappedDocumentForm = {
          _id: item._id,
          title: item.title || "",
          description: "",
          category: item.category || null,
          dueDate: item.date || null,
          studio: item.studio || null, // wird beim Speichern vom DB-Doc beibehalten falls null
          invoiceIssuer: item.invoiceIssuer || null,
          amount: item.amount,
          invoiceNumber: item.invoiceNumber || "",
          date: item.date ? this.$moment(item.date).format("YYYY-MM-DD") : null,
        }
        this.unmappedSelectedInvoiceIssuer = item.invoiceIssuer || null
        this.unmappedDocumentDialog = true
      } catch (err) {
        console.error("Fehler beim Öffnen des Belegs:", err)
      }
    },
    async saveUnmappedDocument(documentData, issuerObj) {
      try {
        const payload = {
          title: documentData.title,
          description: documentData.description,
          category: documentData.category || null,
          dueDate: documentData.dueDate || null,
          invoiceIssuer: issuerObj ? issuerObj._id : null,
          amount: documentData.amount,
          invoiceNumber: documentData.invoiceNumber,
          date: documentData.date,
          studio: documentData.studio,
        }
        await api.documents.updateDocument(documentData._id, payload)
        this.unmappedDocumentDialog = false
        // Controlling neu laden, damit der Beleg ins Mapping wandert
        await this.loadControllingData()
      } catch (err) {
        console.error("Fehler beim Speichern des Belegs:", err)
      }
    },

    // --- BERECHNUNGS-METHODEN (REUSABLE) ---
    computeCalculatedActuals(actualDataObj) {
      const c = {
        members: { start: [], joined: [], left: [], end: [], paused: [], pausedRate: [], fluctuation: [], growth: [], absGrowth: [] },
        revenue: {},
        revenueYearly: {},
        costs: {},
        costsYearly: {},
        kpi: {
          gesamtkosten: new Array(12).fill(0),
          yearlyOpex: 0,
          yearlyJoined: 0,
          yearlyLeft: 0,
          // NEUE KPI FELDER:
          gesamtErloese: new Array(12).fill(0),
          yearlyTotalRevenue: 0,
          ebitda: new Array(12).fill(0),
          yearlyEbitda: 0,
        },
        unmapped: { noCategory: [], unknownCategory: [], invalid: [] },
        unmappedTotals: { noCategory: 0, unknownCategory: 0, invalid: 0 },
      }

      if (!actualDataObj) return c

      // NEU: unmapped vom Backend übernehmen
      if (actualDataObj.unmapped) {
        c.unmapped = {
          noCategory: actualDataObj.unmapped.noCategory || [],
          unknownCategory: actualDataObj.unmapped.unknownCategory || [],
          invalid: actualDataObj.unmapped.invalid || [],
        }
      }
      if (actualDataObj.unmappedTotals) {
        c.unmappedTotals = { ...c.unmappedTotals, ...actualDataObj.unmappedTotals }
      }

      // Costs
      if (actualDataObj.costs) {
        const nonOpexKeys = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]

        this.costItems.forEach((item) => {
          const monthly = actualDataObj.costs[item.key] || new Array(12).fill(0)
          c.costs[item.key] = monthly
          c.costsYearly[item.key] = monthly.reduce((sum, val) => sum + val, 0)

          monthly.forEach((val, i) => {
            if (item.key !== "cogs" && !nonOpexKeys.includes(item.key)) {
              c.kpi.gesamtkosten[i] += val
            }
          })
        })

        c.kpi.yearlyOpex = c.kpi.gesamtkosten.reduce((sum, val) => sum + val, 0)
      }

      // Revenue
      if (actualDataObj.revenue) {
        this.revenueItems.forEach((item) => {
          const monthly = actualDataObj.revenue[item.key] || new Array(12).fill(0)
          c.revenue[item.key] = monthly
          c.revenueYearly[item.key] = monthly.reduce((sum, val) => sum + val, 0)

          // NEU: Umsatz aufsummieren
          monthly.forEach((val, i) => {
            c.kpi.gesamtErloese[i] += val
          })
        })
        // NEU: Jahresumsatz
        c.kpi.yearlyTotalRevenue = c.kpi.gesamtErloese.reduce((sum, val) => sum + val, 0)
      }

      // NEU: EBITDA Berechnung (Erlöse - COGS - Opex)
      for (let i = 0; i < 12; i++) {
        const erloese = c.kpi.gesamtErloese[i] || 0
        const cogs = c.costs["cogs"] ? c.costs["cogs"][i] || 0 : 0
        const opex = c.kpi.gesamtkosten[i] || 0
        c.kpi.ebitda[i] = erloese - cogs - opex
      }
      c.kpi.yearlyEbitda = c.kpi.ebitda.reduce((sum, val) => sum + val, 0)

      // Members
      if (actualDataObj.members) {
        c.members = actualDataObj.members
        // Sum yearlyJoined / yearlyLeft from the members arrays
        if (Array.isArray(actualDataObj.members.joined)) {
          c.kpi.yearlyJoined = actualDataObj.members.joined.reduce((s, v) => s + (v || 0), 0)
        }
        if (Array.isArray(actualDataObj.members.left)) {
          c.kpi.yearlyLeft = actualDataObj.members.left.reduce((s, v) => s + (v || 0), 0)
        }
      }

      return c
    },
    computeCalculatedData(planDataObj) {
      const c = {
        members: [],
        revenue: {},
        revenueYearly: {},
        revenueAvgPerMember: {},
        costs: {},
        costsYearly: {},
        costsMonthly: {},
        kpi: {
          gesamtErloese: [],
          rohertrag: [],
          gesamtkosten: [],
          ebitda: [],
          ebit: [],
          ergebnisNachSteuern: [],
          yearlyJoined: 0,
          yearlyLeft: 0,
          yearlyGrowth: 0,
          yearlyTotalRevenue: 0,
          yearlyOpex: 0,
          yearlyEbitda: 0,
          yearlyEbit: 0,
          yearlyErgebnis: 0,
          yearlyPausedRate: 0,
          yearlyFluctuation: 0,
          yearlyGrowthRate: 0,
        },
      }

      if (!planDataObj) return c
      const pd = planDataObj

      // --- NEU: Startmonat ermitteln ---
      const startMonthKey = pd.startMonth || "jan"
      const startIndex = this.months.findIndex((m) => m.key === startMonthKey)
      const getOperationalMultiplier = (index) => (index >= startIndex ? 1 : 0)
      // ---------------------------------

      // --- GEÄNDERT: currentMembers startet bei 0, wird erst im Startmonat gesetzt ---
      let currentMembers = 0
      let totalMembersYear_End = 0
      let totalMembersYear_Start = 0
      let totalJoinedYear = 0

      // 1. Members
      // --- GEÄNDERT: Von 'for of' zu normalem Loop, damit wir den Index 'i' haben ---
      for (let i = 0; i < 12; i++) {
        const m = this.months[i]
        const isOperational = i >= startIndex

        // --- NEU: Basisbestand wird exakt im Startmonat gesetzt ---
        if (i === startIndex) {
          currentMembers = pd.members.baseJan || 0
        }

        // --- NEU: Werte auf 0 setzen, wenn vor Startmonat ---
        const joined = isOperational ? pd.members.joined[m.key] || 0 : 0
        const left = isOperational ? pd.members.left[m.key] || 0 : 0
        const pausedRate = isOperational ? (pd.members.pausedRate && pd.members.pausedRate[m.key]) || 0 : 0

        const endMembers = isOperational ? currentMembers + joined - left : 0
        const fluctuation = isOperational && currentMembers > 0 ? (left / currentMembers) * 12 : 0
        const growth = isOperational && currentMembers > 0 ? (joined - left) / currentMembers : 0
        const paused = endMembers * (pausedRate / 100)

        c.members.push({
          start: isOperational ? currentMembers : 0, // NEU: start auch auf 0 setzen
          joined,
          left,
          end: endMembers,
          paused,
          pausedRate: pausedRate / 100, // store as decimal for formatPercent
          fluctuation,
          growth,
          absGrowth: joined - left,
        })

        if (isOperational) {
          totalMembersYear_Start += currentMembers
          totalMembersYear_End += endMembers
          totalJoinedYear += joined
          c.kpi.yearlyJoined += joined
          c.kpi.yearlyLeft += left
          c.kpi.yearlyGrowth += joined - left
        }
        currentMembers = endMembers
      }

      const yearlyBaseJan = pd.members.baseJan || 0
      if (yearlyBaseJan > 0) {
        c.kpi.yearlyFluctuation = c.kpi.yearlyLeft / yearlyBaseJan
        c.kpi.yearlyGrowthRate = c.kpi.yearlyGrowth / yearlyBaseJan
      }
      if (totalMembersYear_End > 0) {
        // Average paused rate across the year
        const totalPaused = c.members.reduce((sum, m) => sum + m.paused, 0)
        c.kpi.yearlyPausedRate = totalPaused / totalMembersYear_End
      }

      const distributeByStartMembers = (proMitglied, growthRate, deductPaused) => {
        return this.months.map((m, i) => {
          if (i < startIndex) return 0 // --- NEU ---
          const membersToCount = deductPaused ? c.members[i].start * (1 - c.members[i].pausedRate) : c.members[i].start
          return proMitglied * membersToCount * (1 + (growthRate / 100) * ((i + 1) / 12))
        })
      }
      const distributeByJoined = (proStartpaket, growthRate) => {
        return this.months.map((m, i) => {
          if (i < startIndex) return 0 // --- NEU ---
          return proStartpaket * c.members[i].joined * (1 + (growthRate / 100) * ((i + 1) / 12))
        })
      }
      const distributeFlat = (monthlyVal, growthRate) => {
        return this.months.map((m, i) => {
          if (i < startIndex) return 0 // --- NEU ---
          return monthlyVal * (1 + (growthRate / 100) * ((i + 1) / 12))
        })
      }

      // 2. Revenues
      const revMemberKeys = ["dues", "bistro", "nutrition", "solarium", "shop", "childcare"]
      const revJoinedKeys = ["startupFees"]
      const revMonthly = new Array(12).fill(0)

      this.revenueItems.forEach((item) => {
        const inputVal = pd.revenue[item.key] || 0
        const growthRate = pd.revenue.growthRates ? pd.revenue.growthRates[item.key] || 0 : 0

        let dist = []
        if (revMemberKeys.includes(item.key)) {
          const deductPaused = item.key === "dues" // Only 'dues' deducts paused members according to user logic
          dist = distributeByStartMembers(inputVal, growthRate, deductPaused)
          c.revenueAvgPerMember[item.key] = inputVal
        } else if (revJoinedKeys.includes(item.key)) {
          dist = distributeByJoined(inputVal, growthRate)
          c.revenueAvgPerMember[item.key] = inputVal
        } else {
          dist = distributeFlat(inputVal, growthRate)
          c.revenueAvgPerMember[item.key] = inputVal // Technically not 'per member', but shows the base input value
        }

        // Add specific extra monthly amounts if set
        if (pd.revenue.extraMonthly && pd.revenue.extraMonthly[item.key]) {
          // --- GEÄNDERT: Multiplikator hinzugefügt, falls Heinz versehentlich Werte vor Start einträgt ---
          dist = dist.map((val, i) => val + getOperationalMultiplier(i) * (pd.revenue.extraMonthly[item.key][this.months[i].key] || 0))
        }

        c.revenue[item.key] = dist

        const yearlyTotal = dist.reduce((sum, val) => sum + val, 0)
        c.revenueYearly[item.key] = yearlyTotal
        c.kpi.yearlyTotalRevenue += yearlyTotal
        dist.forEach((val, i) => {
          revMonthly[i] += val
        })
      })

      // 3. Costs
      const costMonthlyArr = new Array(12).fill(0)
      const cogsMonthly = new Array(12).fill(0)

      const nonOpexKeys = ["cogs", "depreciation", "financeCost", "financeIncome", "neutralCost", "neutralIncome", "taxes"]

      this.costItems.forEach((item) => {
        let monthlyVal = 0
        let growthRate = 0
        let extraMonthlyObj = null

        if (item.isNested) {
          monthlyVal = pd.costs[item.isNested]?.[item.nestedKey] || 0
          growthRate = pd.costs.growthRates?.[item.isNested]?.[item.nestedKey] || 0
          extraMonthlyObj = pd.costs.extraMonthly?.[item.isNested]?.[item.nestedKey] || null
        } else {
          monthlyVal = pd.costs[item.key] || 0
          growthRate = pd.costs.growthRates?.[item.key] || 0
          extraMonthlyObj = pd.costs.extraMonthly?.[item.key] || null
        }

        let dist = this.months.map((m, i) => {
          if (i < startIndex) return 0 // --- NEU ---
          return monthlyVal * (1 + (growthRate / 100) * ((i + 1) / 12))
        })

        if (extraMonthlyObj) {
          // --- GEÄNDERT: Multiplikator hinzugefügt ---
          dist = dist.map((val, i) => val + getOperationalMultiplier(i) * (extraMonthlyObj[this.months[i].key] || 0))
        }

        const yearlyCostVal = dist.reduce((sum, val) => sum + val, 0)

        c.costsMonthly[item.key] = monthlyVal
        c.costsYearly[item.key] = yearlyCostVal
        c.costs[item.key] = dist

        dist.forEach((val, i) => {
          if (item.key === "cogs") {
            cogsMonthly[i] += val
          } else if (!nonOpexKeys.includes(item.key)) {
            costMonthlyArr[i] += val
          }
        })
      })

      // 4. KPIs
      for (let i = 0; i < 12; i++) {
        const erloese = revMonthly[i]
        const cogs = cogsMonthly[i]
        const rohertrag = erloese - cogs
        const opex = costMonthlyArr[i]
        const ebitda = rohertrag - opex
        const depr = c.costs["depreciation"][i] || 0
        const ebit = ebitda - depr

        const finInc = c.costs["financeIncome"][i] || 0
        const finCost = c.costs["financeCost"][i] || 0
        const neutInc = c.costs["neutralIncome"][i] || 0
        const neutCost = c.costs["neutralCost"][i] || 0
        const ebt = ebit + finInc - finCost + neutInc - neutCost

        const taxes = c.costs["taxes"][i] || 0
        const nachSteuern = ebt - taxes

        c.kpi.gesamtErloese.push(erloese)
        c.kpi.rohertrag.push(rohertrag)
        c.kpi.gesamtkosten.push(opex)
        c.kpi.ebitda.push(ebitda)
        c.kpi.ebit.push(ebit)
        c.kpi.ergebnisNachSteuern.push(nachSteuern)

        c.kpi.yearlyOpex += opex
        c.kpi.yearlyEbitda += ebitda
        c.kpi.yearlyEbit += ebit
        c.kpi.yearlyErgebnis += nachSteuern
      }

      return c
    },

    // --- NEUE HELPER-METHODEN FÜR DAS DELTA ---
    getDeltaClass(ist, plan, isCost = true) {
      const diff = ist - plan
      if (diff === 0) return "grey--text"
      if (isCost) {
        return diff > 0 ? "text-error" : "text-success" // Bei Kosten: Über Plan = rot, Unter Plan = grün
      }
      return diff > 0 ? "text-success" : "text-error"
    },
    formatDelta(ist, plan) {
      const diff = ist - plan
      if (diff === 0) return ""
      const prefix = diff > 0 ? "+" : ""
      return prefix + this.formatMoney(diff)
    },
    formatNumber(val) {
      return new Intl.NumberFormat("de-CH").format(val)
    },
    formatMoney(val) {
      if (val === undefined || val === null) return "0"
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 0 }).format(Math.round(val))
    },
    formatPercent(val) {
      if (val === undefined || val === null) return "0.0%"
      return (val * 100).toFixed(1) + "%"
    },
    async loadControllingData() {
      this.isControllingLoading = true
      try {
        const studioIds = this.controllingStudioIds
        const plans = await api.planning.fetchPlans({ year: this.controllingYear })
        // p.studioId can be a populated object {_id, name} or a plain string
        const getStudioId = (p) => (p.studioId && typeof p.studioId === "object" ? String(p.studioId._id) : String(p.studioId))

        const selectedPlans = studioIds.length ? plans.filter((p) => studioIds.includes(getStudioId(p))) : plans

        const targetIds = studioIds.length ? studioIds : this.mappedStudios.map((s) => s._id)
        const actualPromises = targetIds.map((sid) => api.planning.fetchActuals({ studioId: sid, year: this.controllingYear }).catch(() => null))
        const actuals = await Promise.all(actualPromises)
        const validActuals = actuals.filter((a) => a)

        const calculatedPlansArray = selectedPlans.map((p) => this.computeCalculatedData(p))
        const calculatedActualsArray = validActuals.map((a) => this.computeCalculatedActuals(a))

        this.controllingCalculatedPlanObj = this.aggregateCalculatedPlan(calculatedPlansArray)
        this.controllingCalculatedActualsObj = this.aggregateCalculatedActuals(calculatedActualsArray)
      } catch (error) {
        console.error("Fehler beim Laden der Controlling-Daten:", error)
      } finally {
        this.isControllingLoading = false
      }
    },
    aggregateCalculatedPlan(calcArray) {
      const result = this.computeCalculatedData(getDefaultPlan()) // empty skeleton
      result.kpi.gesamtErloese = new Array(12).fill(0)
      result.kpi.rohertrag = new Array(12).fill(0)
      result.kpi.gesamtkosten = new Array(12).fill(0)
      result.kpi.ebitda = new Array(12).fill(0)
      result.kpi.ebit = new Array(12).fill(0)
      result.kpi.ergebnisNachSteuern = new Array(12).fill(0)

      for (const calc of calcArray) {
        if (!calc || !calc.members || !Array.isArray(calc.members)) continue
        for (let i = 0; i < 12; i++) {
          result.kpi.gesamtErloese[i] += calc.kpi.gesamtErloese[i] || 0
          result.kpi.rohertrag[i] += calc.kpi.rohertrag[i] || 0
          result.kpi.gesamtkosten[i] += calc.kpi.gesamtkosten[i] || 0
          result.kpi.ebitda[i] += calc.kpi.ebitda[i] || 0
          result.kpi.ebit[i] += calc.kpi.ebit[i] || 0
          result.kpi.ergebnisNachSteuern[i] += calc.kpi.ergebnisNachSteuern[i] || 0

          if (calc.members[i]) {
            result.members[i].start += calc.members[i].start || 0
            result.members[i].joined += calc.members[i].joined || 0
            result.members[i].left += calc.members[i].left || 0
            result.members[i].end += calc.members[i].end || 0
            result.members[i].paused += calc.members[i].paused || 0
            result.members[i].absGrowth += calc.members[i].absGrowth || 0
          }
        }
        result.kpi.yearlyJoined += calc.kpi.yearlyJoined || 0
        result.kpi.yearlyLeft += calc.kpi.yearlyLeft || 0
        result.kpi.yearlyGrowth += calc.kpi.yearlyGrowth || 0
        result.kpi.yearlyTotalRevenue += calc.kpi.yearlyTotalRevenue || 0
        result.kpi.yearlyOpex += calc.kpi.yearlyOpex || 0
        result.kpi.yearlyEbitda += calc.kpi.yearlyEbitda || 0
        result.kpi.yearlyEbit += calc.kpi.yearlyEbit || 0
        result.kpi.yearlyErgebnis += calc.kpi.yearlyErgebnis || 0

        this.revenueItems.forEach((item) => {
          for (let i = 0; i < 12; i++) result.revenue[item.key][i] += (calc.revenue[item.key] && calc.revenue[item.key][i]) || 0
          result.revenueYearly[item.key] += calc.revenueYearly[item.key] || 0
        })
        this.costItems.forEach((item) => {
          for (let i = 0; i < 12; i++) result.costs[item.key][i] += (calc.costs[item.key] && calc.costs[item.key][i]) || 0
          result.costsYearly[item.key] += calc.costsYearly[item.key] || 0
        })
      }
      return result
    },
    aggregateCalculatedActuals(calcArray) {
      const result = this.computeCalculatedActuals(null) // empty skeleton

      result.unmapped = { noCategory: [], unknownCategory: [], invalid: [] }
      result.unmappedTotals = { noCategory: 0, unknownCategory: 0, invalid: 0 }

      // Pre-initialize all revenue/cost keys so we can safely do [i] += below
      this.revenueItems.forEach((item) => {
        result.revenue[item.key] = new Array(12).fill(0)
        result.revenueYearly[item.key] = 0
      })
      this.costItems.forEach((item) => {
        result.costs[item.key] = new Array(12).fill(0)
        result.costsYearly[item.key] = 0
      })
      // Pre-initialize member arrays so [i] += never starts from undefined
      result.members = {
        start: new Array(12).fill(0),
        joined: new Array(12).fill(0),
        left: new Array(12).fill(0),
        end: new Array(12).fill(0),
        paused: new Array(12).fill(0),
        pausedRate: new Array(12).fill(0),
        fluctuation: new Array(12).fill(0),
        growth: new Array(12).fill(0),
        absGrowth: new Array(12).fill(0),
      }

      for (const calc of calcArray) {
        for (let i = 0; i < 12; i++) {
          result.kpi.gesamtkosten[i] += calc.kpi.gesamtkosten[i] || 0

          // NEU: Aggregiere Erlöse und EBITDA pro Monat
          result.kpi.gesamtErloese[i] += calc.kpi.gesamtErloese[i] || 0
          result.kpi.ebitda[i] += calc.kpi.ebitda[i] || 0

          if (calc.members && calc.members.start && Array.isArray(calc.members.start)) {
            result.members.start[i] += calc.members.start[i] || 0
            result.members.joined[i] += calc.members.joined[i] || 0
            result.members.left[i] += calc.members.left[i] || 0
            result.members.end[i] += calc.members.end[i] || 0
            result.members.paused[i] += calc.members.paused[i] || 0
            result.members.absGrowth[i] += calc.members.absGrowth[i] || 0
          }
        }
        result.kpi.yearlyOpex += calc.kpi.yearlyOpex || 0
        result.kpi.yearlyJoined += calc.kpi.yearlyJoined || 0
        result.kpi.yearlyLeft += calc.kpi.yearlyLeft || 0

        result.kpi.yearlyTotalRevenue += calc.kpi.yearlyTotalRevenue || 0
        result.kpi.yearlyEbitda += calc.kpi.yearlyEbitda || 0

        this.revenueItems.forEach((item) => {
          for (let i = 0; i < 12; i++) result.revenue[item.key][i] += (calc.revenue[item.key] && calc.revenue[item.key][i]) || 0
          result.revenueYearly[item.key] += calc.revenueYearly[item.key] || 0
        })
        this.costItems.forEach((item) => {
          for (let i = 0; i < 12; i++) result.costs[item.key][i] += (calc.costs[item.key] && calc.costs[item.key][i]) || 0
          result.costsYearly[item.key] += calc.costsYearly[item.key] || 0
        })

        if (calc.unmapped) {
          result.unmapped.noCategory.push(...(calc.unmapped.noCategory || []))
          result.unmapped.unknownCategory.push(...(calc.unmapped.unknownCategory || []))
          result.unmapped.invalid.push(...(calc.unmapped.invalid || []))
        }
        if (calc.unmappedTotals) {
          result.unmappedTotals.noCategory += calc.unmappedTotals.noCategory || 0
          result.unmappedTotals.unknownCategory += calc.unmappedTotals.unknownCategory || 0
          result.unmappedTotals.invalid += calc.unmappedTotals.invalid || 0
        }
      }
      return result
    },
    async fetchTACTesting() {
      try {
        const queryPayload = {
          sql: `
           SELECT
        c.*,
    ci.Shortname as CHECKINDEVICESHORTNAME,
    co.Shortname as CHECKOUTDEVICESHORTNAME,
    li.RESSORTGROUPID as RESSORTGROUPID,
    cu.EMAIL as EMAIL
    FROM tac.V_Checkins c
    LEFT JOIN tac.V_ACCESSDEVICES ci ON c.CHECKIN_ACCESSDEVICEID = ci.Accessdeviceid
    LEFT JOIN tac.V_ACCESSDEVICES co ON c.CHECKOUT_ACCESSDEVICEID = co.Accessdeviceid
    LEFT JOIN tac.V_LOCATIONS li ON c.LOCATIONID = li.LOCATIONID
    LEFT JOIN tac.V_CUSTOMER cu ON c.CUSTOMERID = cu.CUSTOMERID
    WHERE
        (c.CHECKINDATE >= DATEADD(MINUTE, -25, GETDATE()) OR c.CHECKOUTDATE >= DATEADD(MINUTE, -25, GETDATE()));
          `,
          params: [],
        }

        let test = await api.fetchTACTesting(queryPayload)

        // --- BERECHNUNG IM FRONTEND ---
        const summary = {}
        let totalActiveNet = 0
        let totalActiveGross = 0

        let totalCancelledNet = 0
        let totalCancelledGross = 0
        let cancelledTransactionsCount = 0

        if (test && test.length > 0) {
          test.forEach((row) => {
            const cat = row.CATEGORYNAME || "OHNE KATEGORIE"
            const net = parseFloat(row.FINALNET) || 0
            const gross = parseFloat(row.FINALGROSS) || 0

            // Check ob CANCELLATION true ist (manchmal kommt es als String, Number oder Boolean)
            const isCancelled = row.CANCELLATION === true || row.CANCELLATION === 1 || row.CANCELLATION === "true"

            if (isCancelled) {
              totalCancelledNet += net
              totalCancelledGross += gross
              cancelledTransactionsCount += 1
            } else {
              // Reguläre, aktive Buchungen
              if (!summary[cat]) {
                summary[cat] = { count: 0, netSum: 0, grossSum: 0 }
              }

              summary[cat].count += 1
              summary[cat].netSum += net
              summary[cat].grossSum += gross

              totalActiveNet += net
              totalActiveGross += gross
            }
          })
        }

        const finalResult = {
          info: "Auswertung für Januar 2025 (Frauenfeld)",
          transactionsCount: test.length,
          active: {
            net: totalActiveNet,
            gross: totalActiveGross,
          },
          cancelled: {
            count: cancelledTransactionsCount,
            net: totalCancelledNet,
            gross: totalCancelledGross,
          },
          // Hier vergleichen wir mal testweise, was passiert, wenn wir beides addieren:
          combinedTotalNet: totalActiveNet + totalCancelledNet,
          categories: summary,
        }

        console.log("Ausgewertete TAC Daten:", finalResult)
      } catch (error) {
        console.error("Fehler beim Laden der TAC Test-Daten:", error)
      }
    },

    toggleAllControllingStudios() {
      if (this.controllingStudioIds.length === this.mappedStudios.length) {
        this.controllingStudioIds = []
      } else {
        this.controllingStudioIds = this.mappedStudios.map((s) => s._id)
      }
    },
    toggleAllCockpitStudios() {
      if (this.cockpitStudioIds.length === this.mappedStudios.length) {
        this.cockpitStudioIds = []
      } else {
        this.cockpitStudioIds = this.mappedStudios.map((s) => s._id)
      }
    },
    async loadCockpitData() {
      this.cockpitLoading = true
      this.cockpitPlanAgg = null
      this.cockpitActualsAgg = null
      try {
        // Fetch all studio plans for benchmarking
        const allPlans = await api.planning.fetchPlans({ year: this.cockpitYear })
        // p.studioId may be a populated object {_id, name} – normalise to string
        const getStudioId = (p) => (p.studioId && typeof p.studioId === "object" ? String(p.studioId._id) : String(p.studioId))

        this.allStudiosPlanCalcs = allPlans.map((p) => this.computeCalculatedData(p))

        const selectedIds = this.cockpitStudioIds.length ? this.cockpitStudioIds : this.mappedStudios.map((s) => s._id)

        const selectedPlans = allPlans.filter((p) => selectedIds.includes(getStudioId(p)))
        const selectedCalcs = selectedPlans.map((p) => this.computeCalculatedData(p))
        this.cockpitPlanAgg = this.aggregateCalculatedPlan(selectedCalcs)

        const actualPromises = selectedIds.map((sid) => api.planning.fetchActuals({ studioId: sid, year: this.cockpitYear }).catch(() => null))
        const actuals = await Promise.all(actualPromises)
        const validActuals = actuals.filter((a) => a)
        const actualsCalcs = validActuals.map((a) => this.computeCalculatedActuals(a))
        this.cockpitActualsAgg = this.aggregateCalculatedActuals(actualsCalcs)
      } catch (error) {
        console.error("Fehler beim Laden der Cockpit-Daten:", error)
      } finally {
        this.cockpitLoading = false
      }
    },
    buildBarChartOptions(yLabel, colors, isMoney) {
      const fmtMoney = this.formatMoney
      return {
        chart: { type: "bar", background: "transparent", toolbar: { show: false }, fontFamily: "inherit" },
        theme: { mode: "dark" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "65%" } },
        colors: colors,
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.monthLabels,
          labels: { style: { colors: "#aaa", fontSize: "11px" } },
        },
        yaxis: {
          labels: {
            style: { colors: "#aaa", fontSize: "11px" },
            formatter: isMoney ? (v) => fmtMoney(v) : (v) => v,
          },
        },
        tooltip: {
          theme: "dark",
          y: { formatter: isMoney ? (v) => "CHF " + fmtMoney(v) : (v) => v },
        },
        grid: { borderColor: "rgba(255,255,255,0.08)" },
        legend: { labels: { colors: "#ccc" } },
      }
    },
    buildGaugeOptions(label, color) {
      return {
        chart: { type: "radialBar", background: "transparent", toolbar: { show: false } },
        theme: { mode: "dark" },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: { size: "55%" },
            track: { background: "#333", strokeWidth: "100%" },
            dataLabels: {
              name: { show: true, color: "#aaa", fontSize: "12px", offsetY: -10 },
              value: {
                show: true,
                color: color,
                fontSize: "22px",
                fontWeight: 700,
                offsetY: 5,
                formatter: (v) => v + "%",
              },
            },
          },
        },
        fill: { colors: [color] },
        stroke: { lineCap: "round" },
        labels: [label],
      }
    },

    async loadLocationPlan() {
      try {
        let studioId = this.planData.studioId
        if (this.planData.studioId && typeof this.planData.studioId === "object" && this.planData.studioId._id) {
          studioId = this.planData.studioId._id
        }
        this.fetchTACTesting()
        // BEIDES PARALLEL LADEN (Plan und Ist)
        const [plans, actuals] = await Promise.all([
          api.planning.fetchPlans({ studioId, year: this.planData.year }),
          api.planning.fetchActuals({ studioId, year: this.planData.year }).catch(() => null),
        ])

        this.actualData = actuals // Ist-Zahlen speichern

        if (plans && plans.length > 0) {
          const fetchedPlan = plans[0]
          const defaultPlan = getDefaultPlan()
          this.planData = {
            ...defaultPlan,
            ...fetchedPlan,
            members: {
              ...defaultPlan.members,
              ...(fetchedPlan.members || {}),
              // Ensure joined, left, pausedRate exist
              joined: { ...defaultPlan.members.joined, ...(fetchedPlan.members?.joined || {}) },
              left: { ...defaultPlan.members.left, ...(fetchedPlan.members?.left || {}) },
              pausedRate: { ...defaultPlan.members.pausedRate, ...(fetchedPlan.members?.pausedRate || {}) },
            },
            revenue: {
              ...defaultPlan.revenue,
              ...(fetchedPlan.revenue || {}),
              growthRates: {
                ...defaultPlan.revenue.growthRates,
                ...(fetchedPlan.revenue?.growthRates || {}),
              },
              extraMonthly: {
                ...defaultPlan.revenue.extraMonthly,
                ...(fetchedPlan.revenue?.extraMonthly || {}),
              },
            },
            costs: {
              ...defaultPlan.costs,
              ...(fetchedPlan.costs || {}),
              personnel: {
                ...defaultPlan.costs.personnel,
                ...(fetchedPlan.costs?.personnel || {}),
              },
              growthRates: {
                ...defaultPlan.costs.growthRates,
                ...(fetchedPlan.costs?.growthRates || {}),
                personnel: {
                  ...defaultPlan.costs.growthRates.personnel,
                  ...(fetchedPlan.costs?.growthRates?.personnel || {}),
                },
              },
              extraMonthly: {
                ...defaultPlan.costs.extraMonthly,
                ...(fetchedPlan.costs?.extraMonthly || {}),
                personnel: {
                  ...defaultPlan.costs.extraMonthly.personnel,
                  ...(fetchedPlan.costs?.extraMonthly?.personnel || {}),
                },
              },
            },
          }
        } else {
          const sid = this.planData.studioId
          const y = this.planData.year
          this.planData = getDefaultPlan()
          this.planData.studioId = sid
          this.planData.year = y
        }
        this.isEditing = true
      } catch (error) {
        console.error("Fehler beim Laden des Standortplans:", error)
      }
    },
    async savePlan() {
      this.isSaving = true
      try {
        const dataToSave = JSON.parse(JSON.stringify(this.planData))
        if (dataToSave.studioId && typeof dataToSave.studioId === "object" && dataToSave.studioId._id) {
          dataToSave.studioId = dataToSave.studioId._id
        }

        await api.planning.savePlan(dataToSave)

        // Optional: Die Standort-Daten neu laden, falls gewünscht
        await this.loadLocationPlan()
      } catch (error) {
        console.error("Fehler beim Speichern:", error)
      } finally {
        this.isSaving = false
      }
    },
    openMonthlyDialog(type, item) {
      this.editMonthlyDialog.type = type
      this.editMonthlyDialog.key = item.key
      this.editMonthlyDialog.isNested = item.isNested || null
      this.editMonthlyDialog.nestedKey = item.nestedKey || null
      this.editMonthlyDialog.label = item.label
      this.editMonthlyDialog.isOpen = true
    },
    getEditTarget() {
      const type = this.editMonthlyDialog.type
      const key = this.editMonthlyDialog.key
      const isNested = this.editMonthlyDialog.isNested
      const nestedKey = this.editMonthlyDialog.nestedKey

      if (!type || !key) return {}

      if (isNested && nestedKey) {
        return this.planData[type].extraMonthly[isNested][nestedKey]
      } else {
        return this.planData[type].extraMonthly[key]
      }
    },
    hasExtraMonthly(type, item) {
      if (!this.planData || !this.planData[type] || !this.planData[type].extraMonthly) return false

      let target
      if (item.isNested && item.nestedKey) {
        if (!this.planData[type].extraMonthly[item.isNested]) return false
        target = this.planData[type].extraMonthly[item.isNested][item.nestedKey]
      } else {
        target = this.planData[type].extraMonthly[item.key]
      }

      if (!target) return false

      return Object.values(target).some((val) => val && val !== 0)
    },
  },
  watch: {
    csvImportStatusFilter() {
      this.loadCsvImports()
    },
    activeTab(val) {
      // Tab-Index 3 ist der CSV-Import (nach Standort/Controlling/Cockpit)
      if (val === 3) this.loadCsvImports()
    },
  },
}
</script>

<style scoped>
/* Transparente Hintergründe für die Tabellen-Cards, damit das Dark Theme von der v-card wirkt */
.transparent-table {
  background-color: transparent !important;
}

/* Reduziertes Padding für maximale Übersichtlichkeit */
.v-data-table td,
.v-data-table th {
  padding: 0 8px !important;
  height: 40px !important;
}

/* Text-Fields in der Tabelle extrem kompakt machen */
.compact-input {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 14px;
}
.compact-input >>> .v-input__control > .v-input__slot {
  min-height: 32px !important;
}
.compact-input >>> .v-text-field__details {
  display: none !important;
}

/* Berechnete Ansicht Styles */
.calculated-view .calculated-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
}

.calc-table {
  background-color: #1e1e1e !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr > td {
  font-size: 13px !important;
  height: 32px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.calc-table .v-data-table__wrapper > table > thead > tr > th {
  font-size: 13px !important;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1) !important;
  background-color: #2c2c2c !important;
}

.header-col {
  width: 250px;
  position: sticky;
  left: 0;
  background-color: #252525 !important;
  z-index: 2;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.info-col {
  width: 130px;
  background-color: rgba(0, 0, 0, 0.1) !important;
  border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.calc-table .v-data-table__wrapper > table > thead > tr > th.info-col {
  background-color: #333 !important;
}

.annual-col {
  background-color: rgba(255, 255, 255, 0.04) !important;
  font-weight: 700 !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.highlight-row > td {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.total-row > td {
  background-color: rgba(255, 255, 255, 0.08) !important;
  border-top: 2px solid #555 !important;
  border-bottom: 2px solid #555 !important;
  font-weight: 900 !important;
  font-size: 14px !important;
  color: #fff !important;
}

.calc-table .v-data-table__wrapper > table > tbody > tr.result-row > td {
  background-color: rgba(25, 118, 210, 0.2) !important;
  border-top: 2px solid #1976d2 !important;
  border-bottom: 2px solid #1976d2 !important;
  font-weight: 900 !important;
  font-size: 14px !important;
  color: #fff !important;
}

/* Responsive fix für sticky col */
@media (max-width: 960px) {
  .header-col {
    position: relative;
    width: auto;
    background-color: transparent !important;
  }
}

/* ===== COCKPIT STYLES ===== */
.cockpit-section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #90caf9;
  margin: 16px 0 10px;
  padding-left: 4px;
  border-left: 3px solid #1976d2;
  padding-left: 10px;
}

.cockpit-chart-card {
  border-radius: 10px !important;
  background: #1a1a2e !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  transition: box-shadow 0.2s;
}

.cockpit-chart-card:hover {
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.2) !important;
}

.cockpit-chart-title {
  font-size: 13px !important;
  font-weight: 600 !important;
  padding-bottom: 0 !important;
  color: #e0e0e0 !important;
}

.text-success {
  color: #4caf50 !important;
}
.text-error {
  color: #ef5350 !important;
}

/* ===== CONTROLLING CLICKABLE AND DIALOG STYLES ===== */
.clickable-cell {
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}
.clickable-cell:hover {
  background-color: rgba(25, 118, 210, 0.15) !important;
  box-shadow: inset 0 0 0 1px rgba(25, 118, 210, 0.4) !important;
}
.clickable-cell:active {
  background-color: rgba(25, 118, 210, 0.3) !important;
}

.glass-card {
  background: rgba(24, 28, 43, 0.95) !important;
  backdrop-filter: blur(12px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
}

.secondary-bg {
  background-color: rgba(255, 255, 255, 0.03) !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  border-radius: 8px !important;
}

.list-item-card {
  background: rgba(255, 255, 255, 0.02) !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease;
}

.list-item-card:hover {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: rgba(25, 118, 210, 0.3) !important;
}

.panel-card {
  background: rgba(255, 255, 255, 0.02) !important;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  border-radius: 8px !important;
  overflow: hidden !important;
}

.panel-card ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 16px 16px !important;
}

.detail-table th {
  font-size: 11px !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.detail-row {
  transition: background-color 0.15s ease;
}

.detail-row:hover {
  background-color: rgba(255, 255, 255, 0.02) !important;
}

.font-mono {
  font-family: monospace, Courier, monospace !important;
}
</style>
